"use strict";

/**
 * SharpAPI → value-bet rows for Analysis UI (ValueBetsPage).
 * GET /opportunities/ev — see https://docs.sharpapi.io/en/api-reference/opportunities-ev
 */

function toNum(v, fb = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
}

/** Decimal odds → American (only used when deriving `trueOdds` from `fair_probability`). */
function americanFromDecimal(dec) {
  const d = toNum(dec, null);
  if (d == null || d <= 1) return null;
  if (d >= 2) return Math.round((d - 1) * 100);
  return Math.round(-100 / (d - 1));
}

function safeStr(v) {
  return v == null ? "" : String(v).trim();
}

function normKey(v) {
  return safeStr(v).toLowerCase().replace(/\s+/g, " ").trim();
}

function toBool(v) {
  return v === true;
}

function clamp(n, a, b) {
  const x = Number(n);
  if (!Number.isFinite(x)) return a;
  return Math.max(a, Math.min(b, x));
}

function normalizeBookmakerNameConservative(raw) {
  const s = safeStr(raw);
  if (!s) return "";
  const k = s.toLowerCase().replace(/[^a-z0-9]+/g, "");
  const map = {
    pinnacle: "Pinnacle",
    pin: "Pinnacle",
    betfair: "Betfair",
    betfairstp: "Betfair",
    sbobet: "SBOBET",
    sbo: "SBOBET",
    marathon: "Marathon",
    marathonbet: "Marathon",
    "188bet": "188Bet",
    "188": "188Bet",
  };
  return map[k] || s;
}

function sharpReferenceTierFromBook(sharpBook) {
  const s = normalizeBookmakerNameConservative(sharpBook);
  const k = s.toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (!k) return { tier: "none", score: 0 };
  if (k === "pinnacle") return { tier: "tier1", score: 3 };
  if (k === "betfair") return { tier: "tier1", score: 3 };
  if (k === "sbobet") return { tier: "tier2", score: 2 };
  if (k === "marathon" || k === "marathonbet") return { tier: "tier2", score: 2 };
  if (k === "188bet") return { tier: "tier3", score: 1 };
  return { tier: "other", score: 1 };
}

function marketQualityTierFromRow(row) {
  const mk = normKey(row?.market);
  const sport = normKey(row?.sport);
  const isPlayerProp = toBool(row?.is_player_prop);
  const flags = [];

  if (isPlayerProp) flags.push("player_prop");
  if (sport.includes("esport") || sport.includes("e-sport")) flags.push("esports");

  // Conservative: only identify clearly "core" families; everything else is medium/low.
  const core =
    mk === "moneyline" ||
    mk === "1x2" ||
    mk.includes("spread") ||
    mk.includes("handicap") ||
    mk.includes("over/under") ||
    mk.includes("total");
  const altLike =
    mk.includes("alternate") ||
    mk.includes("alt ") ||
    mk.includes("alternative") ||
    mk.includes("team total") ||
    mk.includes("player") ||
    mk.includes("corner") ||
    mk.includes("cards") ||
    mk.includes("shots") ||
    mk.includes("threes") ||
    mk.includes("strikeouts") ||
    mk.includes("aces");

  if (core && !isPlayerProp) return { tier: "core", score: 2, flags };
  if (!altLike && !isPlayerProp) return { tier: "standard", score: 1, flags };
  return { tier: "niche", score: 0, flags };
}

function normalizeWarningsToArray(w) {
  if (Array.isArray(w)) return w;
  if (w == null) return [];
  if (typeof w === "string") return w.trim() ? [w.trim()] : [];
  return [];
}

function warningsLookStale(warnings) {
  const arr = normalizeWarningsToArray(warnings);
  for (let i = 0; i < arr.length; i += 1) {
    const s = normKey(arr[i]);
    if (!s) continue;
    if (s.includes("stale")) return true;
    if (s.includes("outdated")) return true;
    if (s.includes("old odds")) return true;
    if (s.includes("oldest odds")) return true;
    if (s.includes("not updated")) return true;
  }
  return false;
}

function buildValueDedupKeyFromNormalizedRow(row) {
  if (!row || typeof row !== "object") return "";
  const scope = row.is_live === true || row.isLive === true ? "live" : "prematch";

  // Fixture identity: prefer a structured event/game ID — it is stable across polls
  // and does not require a start-time fallback.
  const structuredFixtureId =
    safeStr(row.fixture_id) ||
    safeStr(row.game_id) ||
    safeStr(row.event_id) ||
    safeStr(row.match_id);

  let fixture = "";
  if (structuredFixtureId) {
    fixture = structuredFixtureId;
  } else {
    // No structured ID available: only fall back to match text (`game`/`match`) when it
    // is paired with a stable match start time (`start_time`). Never use per-poll/volatile
    // fields here (detected_at, timestamp, ts, updatedAt, Date.now(), self-check run time) —
    // those would make the same fixture produce a different key on every poll.
    // Bucket by UTC calendar date derived purely from `start_time` so the same match on the
    // same day always yields the same identity, and a different day yields a different one.
    const matchText = normKey(safeStr(row.game) || safeStr(row.match));
    let dateBucket = "";
    const startTimeRaw = row.start_time;
    if (startTimeRaw != null) {
      let startMs = null;
      if (typeof startTimeRaw === "number") {
        startMs = Number.isFinite(startTimeRaw) ? startTimeRaw : null;
      } else {
        const s = String(startTimeRaw).trim();
        if (s) {
          const parsed = Date.parse(s);
          startMs = Number.isFinite(parsed) ? parsed : null;
        }
      }
      if (startMs != null) {
        const d = new Date(startMs);
        if (!Number.isNaN(d.getTime())) {
          const y = d.getUTCFullYear();
          const m = String(d.getUTCMonth() + 1).padStart(2, "0");
          const day = String(d.getUTCDate()).padStart(2, "0");
          dateBucket = `${y}-${m}-${day}`;
        }
      }
    }
    if (matchText && dateBucket) {
      fixture = `mt:${matchText}|d:${dateBucket}`;
    }
  }
  if (!fixture) return "";

  const book = normalizeBookmakerNameConservative(row.sportsbook || row.bookmaker || row.book);
  if (!book) return "";

  // Canonical market identity: prefer the official, untranslated `market_type`/`marketType`
  // (stable identity) and only fall back to the display-only `market` label when neither
  // structured field exists. This ensures a market *label* change alone does not change the
  // key while an actual `market_type` change does.
  const market = safeStr(row.market_type) || safeStr(row.marketType) || safeStr(row.market);
  if (!market) return "";

  const selection = safeStr(row.selection);
  if (!selection) return "";

  const line = row.line == null ? "" : safeStr(row.line);
  const isPP = row.is_player_prop === true || row.isPlayerProp === true;
  const player = isPP ? safeStr(row.player_name || row.playerName) : "";
  const stat = isPP ? safeStr(row.stat_category || row.statCategory) : "";
  if (isPP && (!player || !stat)) return "";
  const sport = safeStr(row.sport);

  // Must be exact: event/scope/book/market/selection/line + player/stat context when present.
  return [
    "vb",
    scope,
    fixture,
    sport || "",
    book,
    market,
    selection,
    line || "",
    isPP ? "pp:1" : "pp:0",
    isPP ? player : "",
    isPP ? stat : "",
  ]
    .join("\x1f")
    .trim();
}

function computeCommercialQualityScore(row) {
  const flags = [];
  const reject = [];

  const ref = sharpReferenceTierFromBook(row?.sharp_book);
  const hasSharpReference = ref.tier !== "none";
  const marketQ = marketQualityTierFromRow(row);

  const ev = toNum(row?.ev_percentage ?? row?.ev, null);
  const conf = toNum(row?.confidence_score, null);
  const kelly = toNum(row?.kelly_percent, null);
  const bookCount = toNum(row?.book_count, null);
  const width = toNum(row?.market_width, null);
  const possiblyStale = row?.possibly_stale === true;
  const oddsAge = toNum(row?.oldest_odds_age_seconds, null);
  const warnStale = warningsLookStale(row?.warnings);

  let score = 50;

  // Sharp reference
  if (hasSharpReference) {
    score += 6 * ref.score;
    flags.push(`sharp_ref_${ref.tier}`);
  } else {
    score -= 8;
    flags.push("no_sharp_reference");
  }

  // Confidence (0..100 typical; treat null as neutral)
  if (conf != null) {
    const c = clamp(conf, 0, 100);
    score += (c - 50) * 0.22;
    if (c >= 75) flags.push("high_confidence");
  } else {
    flags.push("missing_confidence");
    score -= 2;
  }

  // Kelly: only a small bonus when positive and not extreme.
  if (kelly != null) {
    if (kelly > 0) score += Math.min(6, Math.max(0, kelly) * 0.35);
    if (kelly >= 6) flags.push("high_kelly");
    if (kelly < -1) flags.push("negative_kelly");
  }

  // Market support
  if (bookCount != null) {
    score += Math.min(8, Math.max(0, bookCount - 4) * 0.75);
    if (bookCount >= 10) flags.push("many_books");
  } else {
    flags.push("missing_book_count");
  }

  // Market width: penalize only when clearly wide.
  if (width != null) {
    if (width >= 0.12) {
      score -= 10;
      flags.push("very_wide_market");
    } else if (width >= 0.08) {
      score -= 6;
      flags.push("wide_market");
    } else if (width >= 0.05) {
      score -= 2;
      flags.push("moderately_wide_market");
    }
  }

  // Staleness
  if (possiblyStale) {
    score -= 22;
    flags.push("possibly_stale");
    reject.push("possibly_stale");
  }
  if (warnStale) {
    score -= 14;
    flags.push("stale_warning");
  }
  if (oddsAge != null) {
    if (oddsAge >= 600) {
      score -= 12;
      flags.push("very_old_odds");
    } else if (oddsAge >= 240) {
      score -= 6;
      flags.push("old_odds");
    }
  }

  // Market family: niche penalty but do not hard reject.
  score += marketQ.score * 3;
  if (marketQ.tier === "niche") score -= 7;
  if (marketQ.flags && marketQ.flags.length) flags.push(...marketQ.flags);

  // EV is already a filter upstream; slight bonus for very strong EV.
  if (ev != null) {
    if (ev >= 10) score += 4;
    else if (ev >= 6) score += 2;
  }

  const qualityScore = Math.round(clamp(score, 0, 100));
  return {
    qualityScore,
    commercialFlags: [...new Set(flags)].filter(Boolean),
    commercialRejectReasons: [...new Set(reject)].filter(Boolean),
    hasSharpReference,
    sharpReferenceTier: ref.tier,
    marketQualityTier: marketQ.tier,
  };
}

function enrichCommercialValueBetRow(normalizedRow) {
  if (!normalizedRow || typeof normalizedRow !== "object") return normalizedRow;

  // Preserve backwards compatibility: never remove/rename existing fields.
  if (normalizedRow.qualityScore != null && normalizedRow.valueDedupKey) {
    // Ensure arrays exist and are arrays (defensive)
    const cf = Array.isArray(normalizedRow.commercialFlags) ? normalizedRow.commercialFlags : [];
    const cr = Array.isArray(normalizedRow.commercialRejectReasons)
      ? normalizedRow.commercialRejectReasons
      : [];
    return {
      ...normalizedRow,
      commercialFlags: cf,
      commercialRejectReasons: cr,
      isCommercialSafe: normalizedRow.isCommercialSafe === true,
      hasSharpReference: normalizedRow.hasSharpReference === true,
    };
  }

  const base = computeCommercialQualityScore(normalizedRow);
  const valueDedupKey = buildValueDedupKeyFromNormalizedRow(normalizedRow);

  // Conservative "safe" classification: require a minimum trust baseline, but avoid hiding too much.
  const possiblyStale = normalizedRow.possibly_stale === true;
  const warnStale = warningsLookStale(normalizedRow.warnings);
  const qs = Number.isFinite(Number(base.qualityScore)) ? base.qualityScore : 0;
  const isCommercialSafe = !possiblyStale && !warnStale && qs >= 45;
  const commercialRejectReasons = Array.isArray(base.commercialRejectReasons)
    ? base.commercialRejectReasons.slice()
    : [];
  if (warnStale) commercialRejectReasons.push("stale_warning");

  return {
    ...normalizedRow,
    qualityScore: base.qualityScore,
    isCommercialSafe,
    commercialFlags: base.commercialFlags,
    commercialRejectReasons: [...new Set(commercialRejectReasons)].filter(Boolean),
    hasSharpReference: base.hasSharpReference,
    sharpReferenceTier: base.sharpReferenceTier,
    marketQualityTier: base.marketQualityTier,
    valueDedupKey,
  };
}

function safeParseTimestampMs(v) {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

function isPlainObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** Scalar values only — objects/arrays are skipped (nested keys are read via containers). */
function asPassThroughScalar(v) {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const t = v.trim();
    return t ? t : undefined;
  }
  return undefined;
}

function readScalarFromContainer(container, key) {
  if (!isPlainObject(container)) return undefined;
  return asPassThroughScalar(container[key]);
}

/**
 * First non-empty scalar among aliases, across ordered containers (root → nested).
 * Does not read `game` as a count field (avoids clobbering semantics of fixture `game` elsewhere).
 */
function firstScalarAcrossContainers(containers, aliases) {
  for (let ci = 0; ci < containers.length; ci += 1) {
    const c = containers[ci];
    for (let ai = 0; ai < aliases.length; ai += 1) {
      const v = readScalarFromContainer(c, aliases[ai]);
      if (v !== undefined) return v;
    }
  }
  return undefined;
}

/**
 * Conservative pass-through of live / period context from a raw Sharp +EV row.
 * Only adds scalar fields; never invents values. Omits `game` (fixture title uses `game` on the row).
 * @param {object} raw
 * @returns {Record<string, string|number|boolean>}
 */
function pickPassthroughValueState(raw) {
  if (!raw || typeof raw !== "object") return {};

  const containers = [raw];
  if (isPlainObject(raw.live)) {
    containers.push(raw.live);
    if (isPlainObject(raw.live.scoreboard)) containers.push(raw.live.scoreboard);
    if (isPlainObject(raw.live.timer)) containers.push(raw.live.timer);
  }
  if (isPlainObject(raw.timer)) containers.push(raw.timer);
  if (isPlainObject(raw.scoreboard)) containers.push(raw.scoreboard);
  if (isPlainObject(raw.game_state)) containers.push(raw.game_state);
  if (isPlainObject(raw.gameState)) containers.push(raw.gameState);
  if (isPlainObject(raw.period)) containers.push(raw.period);

  const out = {};
  const put = (outKey, aliases) => {
    const v = firstScalarAcrossContainers(containers, aliases);
    if (v !== undefined) out[outKey] = v;
  };

  put("status", ["status"]);
  put("match_status", ["match_status", "matchStatus"]);
  put("state", ["state"]);
  put("phase", ["phase"]);
  put("status_text", ["status_text", "statusText"]);
  put("game_status", ["game_status", "gameStatus"]);
  put("game_state", ["game_state", "gameState"]);
  put("period", ["period"]);
  put("current_period", ["current_period", "currentPeriod"]);
  put("half", ["half"]);
  put("quarter", ["quarter"]);
  put("current_quarter", ["current_quarter", "currentQuarter"]);
  put("set", ["set"]);
  put("current_set", ["current_set", "currentSet"]);
  put("setNumber", ["setNumber", "set_number"]);
  put("map", ["map"]);
  put("currentMap", ["currentMap", "current_map"]);
  put("mapNumber", ["mapNumber", "map_number"]);
  put("gameNumber", ["gameNumber", "game_number"]);
  put("minute", ["minute"]);
  put("elapsed", ["elapsed"]);
  put("clock", ["clock"]);
  put("match_clock", ["match_clock", "matchClock"]);
  put("time_remaining", ["time_remaining", "timeRemaining"]);
  put("live", ["live"]);
  if (isPlainObject(raw.timer)) {
    const tScalar = asPassThroughScalar(raw.timer);
    if (tScalar !== undefined && out.timer === undefined) out.timer = tScalar;
  } else {
    put("timer", ["timer"]);
  }

  return out;
}

/**
 * Raw Sharp +EV row (minimal shape from REST).
 * @param {object} raw
 */
function normalizeValueBetRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = String(raw.id || "").trim();
  if (!id) return null;

  // Preserve official Sharp structured fields (source of truth for Value semantics).
  const officialMarketType = String(raw.market_type ?? raw.marketType ?? "").trim();
  const officialSelectionType = String(raw.selection_type ?? raw.selectionType ?? "").trim();
  const displayMarket = String(raw.market ?? raw.display_market ?? officialMarketType).trim();
  const displaySelection = String(raw.selection ?? raw.display_selection ?? "").trim();
  const officialMarketId = asPassThroughScalar(raw.market_id) ?? asPassThroughScalar(raw.marketId);

  const gameId = raw.game_id != null ? String(raw.game_id).trim() : "";
  let homeTeam = raw.home_team != null ? String(raw.home_team).trim() : "";
  let awayTeam = raw.away_team != null ? String(raw.away_team).trim() : "";
  if (!homeTeam || !awayTeam) {
    const gameStr = String(raw.game || "").trim();
    if (gameStr) {
      // American format: "Away @ Home"
      const mAt = /^(.+?)\s+@\s+(.+)$/i.exec(gameStr);
      if (mAt) {
        if (!homeTeam) homeTeam = mAt[2].trim();
        if (!awayTeam) awayTeam = mAt[1].trim();
      } else {
        // European format: "Home vs Away" / "Home v Away" / "Home – Away" / "Home - Away"
        const mVs =
          /^(.+?)\s+vs\.?\s+(.+)$/i.exec(gameStr) ||
          /^(.+?)\s+v\s+(.+)$/i.exec(gameStr) ||
          /^(.+?)\s+[–\-]\s+(.+)$/.exec(gameStr);
        if (mVs) {
          if (!homeTeam) homeTeam = mVs[1].trim();
          if (!awayTeam) awayTeam = mVs[2].trim();
        }
      }
    }
  }
  const match =
    String(raw.game || "").trim() ||
    (homeTeam && awayTeam ? `${homeTeam} vs ${awayTeam}` : homeTeam || awayTeam || "");

  const evPercentage = toNum(raw.ev_percentage ?? raw.ev_percent, 0) || 0;
  const oddsDecimal = toNum(raw.odds_decimal, null);
  const oddsAmerican = toNum(raw.odds_american, null);
  const noVigOdds = toNum(raw.no_vig_odds, null);
  const fairProbability = toNum(raw.fair_probability, null);
  /** `trueOdds` is always American: prefer Sharp `no_vig_odds` as American, else convert from fair probability. */
  const trueOdds =
    noVigOdds != null
      ? noVigOdds
      : fairProbability != null && fairProbability > 0 && fairProbability <= 1
        ? americanFromDecimal(1 / fairProbability)
        : null;

  const isLive = raw.is_live === true;
  const detectedAtMs = safeParseTimestampMs(raw.detected_at);
  const warnings = Array.isArray(raw.warnings)
    ? raw.warnings
    : raw.warnings == null
      ? raw.warnings
      : [];

  const valueStatePass = pickPassthroughValueState(raw);

  const normalized = {
    ...raw,
    ...valueStatePass,
    // Official /opportunities/ev fields (explicit; matches SharpAPI EV row contract)
    id,
    game_id: gameId || raw.game_id,
    ev_percentage: evPercentage,
    odds_american: oddsAmerican,
    odds_decimal: oddsDecimal,
    no_vig_odds: noVigOdds,
    fair_probability: fairProbability,
    market_width: toNum(raw.market_width, null),
    devig_method: raw.devig_method ?? null,
    sharp_book: raw.sharp_book ?? null,
    selection: displaySelection,
    market: displayMarket,
    line: raw.line ?? null,
    sportsbook: String(raw.sportsbook || "").trim(),
    game: String(raw.game || "").trim(),
    sport: String(raw.sport || "").trim(),
    league: String(raw.league || "").trim(),
    home_team: homeTeam,
    away_team: awayTeam,
    start_time: raw.start_time ?? null,
    is_live: isLive,
    confidence_score: raw.confidence_score,
    kelly_percent: raw.kelly_percent,
    book_count: raw.book_count,
    arb_available: raw.arb_available,
    arb_profit: raw.arb_profit,
    is_player_prop: raw.is_player_prop,
    player_name: raw.player_name,
    stat_category: raw.stat_category,
    possibly_stale: raw.possibly_stale,
    oldest_odds_age_seconds: raw.oldest_odds_age_seconds,
    warnings,
    detected_at: raw.detected_at,
    bet_id: id,
    valueBetId: id,
    fixture_id: raw.fixture_id ?? (gameId || null),
    event_id: String(raw.event_id ?? raw.game_id ?? raw.fixture_id ?? "").trim() || undefined,
    match,
    // Critical: never overwrite `market_type` with display-only `market`.
    market_type: officialMarketType || displayMarket,
    marketType: officialMarketType || displayMarket,
    market_id: officialMarketId ?? raw.market_id ?? undefined,
    marketId: officialMarketId ?? raw.marketId ?? undefined,
    selection_type: officialSelectionType,
    selectionType: officialSelectionType,
    market: displayMarket,
    display_market: displayMarket,
    displayMarket: displayMarket,
    selection: displaySelection,
    display_selection: displaySelection,
    displaySelection: displaySelection,
    bookmaker: String(raw.sportsbook || "").trim(),
    // Normalized aliases / UI expectations
    ev: evPercentage,
    EV: evPercentage,
    odds: oddsDecimal,
    oddsDecimal,
    oddsAmerican,
    noVigOdds,
    fairProbability,
    marketWidth: toNum(raw.market_width, null),
    devigMethod: raw.devig_method ?? null,
    trueOdds,
    isLive,
    timestamp: detectedAtMs,
    ts: detectedAtMs,
    _source: "sharpapi",
  };
  return enrichCommercialValueBetRow(normalized);
}

/**
 * Raw +EV opportunities (Steam engine merges with /splits + /odds/delta).
 * @param {import('axios').AxiosInstance} client
 * @param {{ live: boolean, limit?: number, min_ev?: number }} opts
 * @returns {Promise<object[]>}
 */
async function fetchRawEvOpportunities(client, opts) {
  const live = opts.live === true;
  const limit = Math.min(200, Math.max(1, Number(opts.limit) || 200));
  const min_ev = toNum(opts.min_ev, 0) ?? 0;

  const { data } = await client.get("/opportunities/ev", {
    params: {
      live,
      limit,
      min_ev,
    },
  });

  if (data && data.success === false) return [];
  if (!data || !Array.isArray(data.data)) return [];
  return data.data;
}

/**
 * @param {import('axios').AxiosInstance} client
 * @param {{ live: boolean, limit?: number, min_ev?: number }} opts
 * @returns {Promise<object[]>}
 */
async function fetchSharpValueBetList(client, opts) {
  const raw = await fetchRawEvOpportunities(client, opts);
  return raw.map(normalizeValueBetRow).filter(Boolean);
}

// --- Value preflight: strict UI row vs matchedRaw (click-time safety) ---

function valueBetRowIsLive(bet) {
  if (!bet || typeof bet !== "object") return false;
  if (bet.isLive === true) return true;
  if (bet.is_live === true) return true;
  if (String(bet.scope || "").trim().toLowerCase() === "live") return true;
  return false;
}

/** HK-style display (0.xx) → decimal price; already-decimal (>1) passthrough. */
function extractDecimalOddsFromValueSide(o) {
  if (!o || typeof o !== "object") return null;
  const nested =
    o.odds && typeof o.odds === "object" && !Array.isArray(o.odds)
      ? toNum(o.odds.decimal, null)
      : null;
  if (nested != null && Number.isFinite(nested) && nested > 1) return nested;
  const raw = o.odds_decimal ?? o.oddsDecimal ?? o.odds ?? null;
  const n = toNum(raw, null);
  if (n == null || !Number.isFinite(n)) return null;
  if (n >= 100) return 1 + n / 100;
  if (n <= -100) return 1 + 100 / Math.abs(n);
  if (n > 0 && n < 1) return 1 + n;
  if (n > 1) return n;
  return null;
}

function extractLineFromValueSide(o) {
  if (!o || typeof o !== "object") return "";
  const v = o.line ?? o?.market?.line ?? o?.selection?.line ?? o.point ?? o.handicap ?? o.total ?? "";
  return v == null ? "" : String(v).trim();
}

function pickComparableMarketFromBet(bet) {
  if (!bet || typeof bet !== "object") return "";
  const m = String(bet.market ?? "").trim();
  const mt = String(bet.market_type ?? bet.marketType ?? "").trim();
  if (!mt) return m;
  if (!m) return mt;
  const mLow = m.toLowerCase();
  const plainSel =
    /^(over|under)$/.test(mLow) ||
    /^(home|away)[_\s]+(over|under)$/.test(mLow) ||
    /^(over|under)\s+[-+]?\d/.test(mLow);
  if (plainSel) return mt;
  const structuredMt = mt.includes("_") || /^(1st|2nd|first|second)\b/i.test(mt);
  if (structuredMt) return mt;
  if (/\b(1st|2nd|first|second)\s+half\b/i.test(m) && !/\b(1st|2nd|first|second)\s+half\b/i.test(mt)) return m;
  return mt || m;
}

function marketTypeRequiresStrictLine(marketStr) {
  const s = normKey(marketStr);
  if (!s) return false;
  if (s.includes("moneyline") || s === "1x2" || s.includes("winner")) return false;
  return (
    s.includes("spread") ||
    s.includes("handicap") ||
    s.includes("total") ||
    s.includes("over_under") ||
    s.includes("over/under") ||
    s.includes("asian") ||
    s.includes("team_total") ||
    s.includes("point")
  );
}

function numericLineEqual(a, b, eps = 1e-6) {
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return Math.abs(x - y) <= eps;
}

/**
 * Conservative: Saba + live + NBA/basketball + Q1 / 1st quarter + spread or total-like market.
 * Used to avoid treating cached raw verification as click-time Ready without a fresh preflight.
 */
function isSabaLiveNbaQ1PeriodValueRow(bet) {
  if (!bet || typeof bet !== "object") return false;
  if (!valueBetRowIsLive(bet)) return false;
  const rawBook = String(bet.sportsbook ?? bet.bookmaker ?? bet.book ?? "").trim();
  const bookKey = normalizeBookmakerNameConservative(rawBook).toLowerCase().replace(/[^a-z0-9]+/g, "");
  const isSaba = bookKey.includes("saba") || rawBook.toLowerCase().includes("saba");
  if (!isSaba) return false;
  const sport = normKey(bet.sport || "");
  const league = normKey(bet.league || "");
  const nbaLike =
    sport.includes("basketball") ||
    sport.includes("basket") ||
    league.includes("nba") ||
    league.includes("national basketball");
  if (!nbaLike) return false;
  const mt = normKey(
    `${bet.market_type || ""} ${bet.marketType || ""} ${bet.market || ""} ${bet.display_market || ""} ${bet.displayMarket || ""}`
  );
  const q1 =
    /\bq\s*1\b/.test(mt) ||
    /\b1st\s*quarter\b/.test(mt) ||
    /\bfirst\s*quarter\b/.test(mt) ||
    /\bquarter\s*1\b/.test(mt);
  if (!q1) return false;
  const spreadTotal =
    mt.includes("spread") ||
    mt.includes("handicap") ||
    mt.includes("total") ||
    mt.includes("over") ||
    mt.includes("under") ||
    mt.includes("asian");
  return spreadTotal;
}

function valuePreflightServerContractOk(response) {
  if (!response || typeof response !== "object") return false;
  if (response.ok !== true) return false;
  if (response.canBet !== true) return false;
  if (String(response.status || "").trim() !== "preflight_verified") return false;
  const mr = response.matchedRaw;
  if (!mr || typeof mr !== "object" || Array.isArray(mr)) return false;
  const ov = response.offerVerification;
  if (!ov || typeof ov !== "object") return false;
  if (String(ov.status || "").trim() !== "verified") return false;
  if (String(ov.verificationLevel || "").trim() !== "raw") return false;
  return true;
}

/**
 * Strict comparison between the opportunity row shown in the UI and preflight `matchedRaw`.
 * @returns {{ ok: boolean, reason: string, details: Record<string, unknown> }}
 */
function validateValuePreflightMatchedRaw(bet, response) {
  const details = {};
  if (!bet || typeof bet !== "object") {
    return { ok: false, reason: "missing_bet", details };
  }
  if (!response || typeof response !== "object") {
    return { ok: false, reason: "missing_response", details };
  }
  const mr = response.matchedRaw;
  if (!mr || typeof mr !== "object" || Array.isArray(mr)) {
    return { ok: false, reason: "missing_matched_raw", details };
  }

  const rowBook = String(bet.sportsbook ?? bet.bookmaker ?? bet.book ?? "").trim();
  const rawBook = String(mr.sportsbook ?? mr.bookmaker ?? "").trim();
  const nbRow = normalizeBookmakerNameConservative(rowBook).toLowerCase().replace(/[^a-z0-9]+/g, "");
  const nbRaw = normalizeBookmakerNameConservative(rawBook).toLowerCase().replace(/[^a-z0-9]+/g, "");
  details.rowBook = rowBook;
  details.matchedBook = rawBook;
  if (!nbRow || !nbRaw || nbRow !== nbRaw) {
    return { ok: false, reason: "sportsbook_mismatch", details };
  }

  const rowMarket = pickComparableMarketFromBet(bet);
  const rawMarket = String(mr.market_type ?? mr.marketType ?? mr.market ?? "").trim();
  details.rowMarket = rowMarket;
  details.matchedMarket = rawMarket;
  if (normKey(rowMarket) !== normKey(rawMarket)) {
    return { ok: false, reason: "market_mismatch", details };
  }

  const rowSel = String(bet.selection ?? bet.display_selection ?? bet.displaySelection ?? "").trim();
  const rawSel = String(mr.selection ?? "").trim();
  details.rowSelection = rowSel;
  details.matchedSelection = rawSel;
  if (normKey(rowSel) !== normKey(rawSel)) {
    return { ok: false, reason: "selection_mismatch", details };
  }

  const mtForLine = rowMarket || rawMarket;
  if (marketTypeRequiresStrictLine(mtForLine)) {
    const rowLine = extractLineFromValueSide(bet);
    const rawLine = extractLineFromValueSide(mr);
    details.rowLine = rowLine;
    details.matchedLine = rawLine;
    if (rowLine === "" || rawLine === "") {
      return { ok: false, reason: "line_missing", details };
    }
    if (!numericLineEqual(rowLine, rawLine)) {
      return { ok: false, reason: "line_mismatch", details };
    }
  }

  const rowDec = extractDecimalOddsFromValueSide(bet);
  const rawDec = extractDecimalOddsFromValueSide(mr);
  details.rowOddsDecimal = rowDec;
  details.matchedOddsDecimal = rawDec;
  if (rowDec == null || !Number.isFinite(rowDec) || rowDec <= 1) {
    return { ok: false, reason: "row_odds_missing", details };
  }
  if (rawDec == null || !Number.isFinite(rawDec) || rawDec <= 1) {
    return { ok: false, reason: "matched_odds_missing", details };
  }
  if (Math.abs(rowDec - rawDec) > 0.01) {
    return { ok: false, reason: "odds_mismatch", details };
  }

  return { ok: true, reason: "", details };
}

/** Copy payload built only from verified preflight matched raw (+ safe fixture context). */
function buildVerifiedValueCopyText(bet, matchedRaw) {
  const book = String(matchedRaw.sportsbook ?? matchedRaw.bookmaker ?? "").trim() || "—";
  const market = String(matchedRaw.market_type ?? matchedRaw.marketType ?? matchedRaw.market ?? "").trim() || "—";
  const sel = String(matchedRaw.selection ?? "").trim() || "—";
  const line = extractLineFromValueSide(matchedRaw);
  const dec = extractDecimalOddsFromValueSide(matchedRaw);
  const hk = dec != null && dec > 1 ? dec - 1 : null;
  const match = bet && typeof bet === "object" ? String(bet.match || "").trim() || "—" : "—";
  const league = bet && typeof bet === "object" ? String(bet.league || "").trim() || "—" : "—";
  return [
    `Match: ${match}`,
    `League: ${league}`,
    `Market: ${market}`,
    `Selection: ${sel}`,
    `Line: ${line || "—"}`,
    `Book cược: ${book}`,
    `Tỷ lệ cược (decimal): ${dec != null ? dec.toFixed(3) : "—"}`,
    `Tỷ lệ cược (HK): ${hk != null ? hk.toFixed(3) : "—"}`,
    `Verified: preflight matchedRaw`,
  ].join("\n");
}

function firstNonEmptyString(values) {
  for (let i = 0; i < values.length; i += 1) {
    const s = safeStr(values[i]);
    if (s) return s;
  }
  return "";
}

/** Lowercase + collapse separators to spaces so `\b` boundaries work for snake_case keys. */
function normalizeMarketHay(value) {
  return safeStr(value)
    .toLowerCase()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Best-effort snake_case → "Title Case" humanization (no translation). */
function humanizeSnakeCaseForDisplay(value) {
  const raw = safeStr(value);
  if (!raw) return "";
  if (!/[_\s-]/.test(raw)) return raw;
  return raw
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function valueLabelLooksRawSnakeCase(label) {
  const s = safeStr(label);
  if (!s) return false;
  if (/_/.test(s)) return true;
  if (
    /\b(total[_\s-]*corners|team[_\s-]*total[_\s-]*corners|1st[_\s-]*half[_\s-]*total[_\s-]*corners|2nd[_\s-]*half[_\s-]*total[_\s-]*corners|total[_\s-]*goals|team[_\s-]*total[_\s-]*goals|total[_\s-]*cards|team[_\s-]*total[_\s-]*cards|moneyline|match[_\s-]*winner|point[_\s-]*spread|asian[_\s-]*handicap|set[_\s-]*handicap|game[_\s-]*spread)\b/i.test(
      s
    )
  ) {
    return true;
  }
  return false;
}

function readValueDisplayPeriodScope(bet) {
  const src = normalizeMarketHay(
    [
      bet?.period,
      bet?.quarter,
      bet?.half,
      bet?.map,
      bet?.set,
      bet?.current_quarter,
      bet?.currentQuarter,
      bet?.current_set,
      bet?.currentSet,
      bet?.currentMap,
      bet?.current_map,
      bet?.market_type,
      bet?.marketType,
      bet?.display_market,
      bet?.displayMarket,
      bet?.market,
    ]
      .map((v) => safeStr(v))
      .filter(Boolean)
      .join(" ")
  );

  if (!src) return "";
  if (/\b(q1|1st quarter|first quarter|quarter 1)\b/.test(src)) return "Q1";
  if (/\b(q2|2nd quarter|second quarter|quarter 2)\b/.test(src)) return "Q2";
  if (/\b(q3|3rd quarter|third quarter|quarter 3)\b/.test(src)) return "Q3";
  if (/\b(q4|4th quarter|fourth quarter|quarter 4)\b/.test(src)) return "Q4";
  if (/\b(map 1|1st map|first map)\b/.test(src)) return "Map 1";
  if (/\b(map 2|2nd map|second map)\b/.test(src)) return "Map 2";
  if (/\b(set 1|1st set|first set)\b/.test(src)) return "Set 1";
  if (/\b(set 2|2nd set|second set)\b/.test(src)) return "Set 2";
  if (/\b(ht|1h|1st half|first half)\b/.test(src)) return "HT";
  if (/\b(2h|2nd half|second half)\b/.test(src)) return "2H";
  if (/\b(ft|full time|fulltime|full match)\b/.test(src)) return "FT";
  return "";
}

/** Returns one of: "moneyline" | "spread" | "team_total" | "total" | "player_prop" | "". */
function inferValueMarketFamily(bet) {
  const hay = normalizeMarketHay(
    `${pickValueDisplayMarketSource(bet)} ${bet?.market_type || ""} ${bet?.marketType || ""}`
  );
  if (!hay) return "";

  if (/\b(player\b|player\s+\w+)\b/.test(hay)) return "player_prop";

  if (
    /\b(moneyline|match winner|winner|h2h|1x2|draw no bet|money line)\b/.test(hay)
  ) return "moneyline";
  if (
    /\b(spread|handicap|asian handicap|european handicap|run line|puck line|point spread|set handicap|game spread|game handicap)\b/.test(
      hay
    )
  ) return "spread";

  if (
    /\b(team total|team totals|team cards|team corners|team total goals|team total corners|team total cards|team total points)\b/.test(
      hay
    )
  ) return "team_total";

  if (
    /\b(total|totals|total points|total goals|total runs|total games|total sets|total corners|total cards|over under|over\/under|cards over under|corners over under)\b/.test(
      hay
    )
  ) return "total";

  return "";
}

function formatSignedValueLine(lineRaw) {
  const raw = safeStr(lineRaw);
  if (!raw) return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  if (n > 0) return `+${n}`;
  if (Object.is(n, -0) || n === 0) return "0";
  return String(n);
}

function formatUnsignedValueLine(lineRaw) {
  const raw = safeStr(lineRaw);
  if (!raw) return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return String(Math.abs(n));
}

function valueSelectionAlreadyContainsLine(selection, line) {
  const s = safeStr(selection).toLowerCase();
  const l = safeStr(line).toLowerCase();
  if (!s || !l) return false;
  return s.includes(` ${l}`) || s.endsWith(l);
}

function pickValueDisplayMarketSource(bet) {
  return firstNonEmptyString([
    bet?.market_type,
    bet?.marketType,
    bet?.display_market,
    bet?.displayMarket,
    bet?.market,
  ]);
}

function pickValueDisplaySelectionSource(bet) {
  return firstNonEmptyString([
    bet?.selection,
    bet?.display_selection,
    bet?.displaySelection,
    bet?.selection_type,
    bet?.selectionType,
  ]);
}

function pickValueDisplayLine(bet) {
  return firstNonEmptyString([
    bet?.line,
    bet?.market?.line,
    bet?.selection?.line,
    bet?.point,
    bet?.handicap,
    bet?.total,
  ]);
}

/**
 * Pure semantic market label for the Value card.
 *
 * Returns "" when the market family cannot be classified — callers must NOT
 * fall back to raw `market_type` snake_case here. `analysisTranslate` builders
 * remain the canonical translation source; this helper only hands back a clean
 * Vietnamese / English family label suitable for fallback display.
 */
function buildValueSemanticMarketLabel(bet, lang) {
  const language = String(lang || "en").toLowerCase();
  const isVi = language.startsWith("vi");
  const marketFamily = inferValueMarketFamily(bet);
  if (!marketFamily) return "";

  let label = "";
  if (marketFamily === "moneyline") label = isVi ? "Kèo thắng" : "Moneyline";
  else if (marketFamily === "spread") label = isVi ? "Cược chấp" : "Spread / Handicap";
  else if (marketFamily === "total") label = isVi ? "Tài/Xỉu" : "Total";
  else if (marketFamily === "team_total") label = isVi ? "Tổng điểm đội" : "Team Total";
  else if (marketFamily === "player_prop") return "";
  if (!label) return "";

  const scope = readValueDisplayPeriodScope(bet);
  if (!scope) return label;
  if (new RegExp(`\\b${scope.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(label)) {
    return label;
  }
  return `${label} ${scope}`.trim();
}

/**
 * Pure semantic selection label.
 *
 * - Totals: unsigned line (e.g. "Xỉu 4.5", "Tài 10.5").
 * - Spread/handicap: signed line (e.g. "Vitality -1.5", "Southampton -1.5").
 * - Moneyline: just the team name when known.
 * - Team total: requires a trusted side; otherwise returns "" so the
 *   safer team-total fallback path takes over.
 */
function buildValueSemanticSelectionLabel(bet, lang) {
  const language = String(lang || "en").toLowerCase();
  const isVi = language.startsWith("vi");
  const selRaw = pickValueDisplaySelectionSource(bet);
  const lineRaw = pickValueDisplayLine(bet);
  const lineSigned = formatSignedValueLine(lineRaw);
  const lineUnsigned = formatUnsignedValueLine(lineRaw);
  const marketFamily = inferValueMarketFamily(bet);
  const home = safeStr(bet?.home_team || bet?.homeTeam);
  const away = safeStr(bet?.away_team || bet?.awayTeam);
  const low = selRaw.toLowerCase();

  const ouWord =
    low === "over" || /\bover\b/.test(low)
      ? isVi
        ? "Tài"
        : "Over"
      : low === "under" || /\bunder\b/.test(low)
        ? isVi
          ? "Xỉu"
          : "Under"
        : "";

  const mappedSide =
    low === "home"
      ? home
      : low === "away"
        ? away
        : low === "draw"
          ? isVi
            ? "Hòa"
            : "Draw"
          : "";
  const baseSelection = mappedSide || selRaw;

  if (marketFamily === "moneyline") {
    return baseSelection || "";
  }

  if (marketFamily === "team_total") {
    const sideTeam = firstNonEmptyString([
      mappedSide,
      home && low.includes(home.toLowerCase()) ? home : "",
      away && low.includes(away.toLowerCase()) ? away : "",
    ]);
    if (!sideTeam) return "";
    if (ouWord && lineUnsigned) return `${sideTeam} ${ouWord} ${lineUnsigned}`.replace(/\s+/g, " ").trim();
    if (lineUnsigned && !valueSelectionAlreadyContainsLine(sideTeam, lineUnsigned)) {
      return `${sideTeam} ${lineUnsigned}`.trim();
    }
    return sideTeam;
  }

  if (marketFamily === "total") {
    if (ouWord && lineUnsigned) return `${ouWord} ${lineUnsigned}`.trim();
    if (ouWord) return ouWord;
    return "";
  }

  if (marketFamily === "spread") {
    if (baseSelection && lineSigned && !valueSelectionAlreadyContainsLine(baseSelection, lineSigned)) {
      return `${baseSelection} ${lineSigned}`.replace(/\s+/g, " ").trim();
    }
    if (baseSelection) return baseSelection;
  }

  return "";
}

function normalizeAnyOddsToDecimal(value) {
  const n = toNum(value, null);
  if (n == null || !Number.isFinite(n)) return null;
  if (n <= -100) return 1 + 100 / Math.abs(n);
  if (n >= 100) return 1 + n / 100;
  if (n > 0 && n < 1) return 1 + n;
  if (n > 1) return n;
  return null;
}

function formatHkFromDecimal(dec) {
  if (!Number.isFinite(dec) || dec <= 1) return "";
  const hk = dec - 1;
  if (!Number.isFinite(hk) || hk <= 0) return "";
  return hk.toFixed(3).replace(/(?:\.0+|(\.\d*?)0+)$/, "$1");
}

function buildValueDisplayOddsHk(bet) {
  const dec = normalizeAnyOddsToDecimal(
    bet?.odds_decimal ?? bet?.oddsDecimal ?? bet?.odds_american ?? bet?.oddsAmerican ?? bet?.odds
  );
  return formatHkFromDecimal(dec);
}

function buildValueDisplayTrueOddsHk(bet) {
  const dec = normalizeAnyOddsToDecimal(
    bet?.trueOdds ?? bet?.no_vig_odds ?? bet?.noVigOdds ?? bet?.true_odds ?? bet?.fairOdds
  );
  return formatHkFromDecimal(dec);
}

// ---------------------------------------------------------------------------
// Generic Value display helpers (Task B / D).
//
// These helpers produce the canonical Value card market header and selection
// label using the SharpAPI EV row contract (market_type / market / selection /
// selection_type / line) plus any `verifiedRaw` payload coming from the
// /preflight matchedRaw response (offerVerification.matched* fields).
//
// Rules:
//   • Handicap / spread family ⇒ "<Team> <signed line>" (e.g. "Uno Mille -1.5")
//   • Total / over-under family ⇒ "<Tài|Xỉu> <unsigned line>"
//   • Team total family         ⇒ "<Team> <Tài|Xỉu> <unsigned line>"
//   • Moneyline / 1x2 / winner  ⇒ "<Team>" (or "Hòa" / "Draw"). No line.
//
// Sources are merged with this priority:
//   1. verifiedRaw.matched* (preflight raw match) — exact sportsbook offer.
//   2. row.* (opportunity feed) — only when raw verification is unavailable.
// We never mix sources across odds / line: if odds come from raw, line must
// also come from raw.
// ---------------------------------------------------------------------------

const VALUE_HANDICAP_LIKE_KEYS = new Set([
  "point_spread",
  "spread",
  "handicap",
  "asian_handicap",
  "european_handicap",
  "game_spread",
  "game_handicap",
  "set_handicap",
  "run_line",
  "puck_line",
  "corner_handicap",
  "corners_handicap",
  "asian_handicap_corners",
  "corners_asian_handicap",
  "1st_half_point_spread",
  "2nd_half_point_spread",
  "1st_half_asian_handicap",
  "2nd_half_asian_handicap",
  "1st_half_run_line",
  "2nd_half_run_line",
  "1st_half_puck_line",
  "2nd_half_puck_line",
]);

const VALUE_TEAM_TOTAL_KEYS = new Set([
  "team_total",
  "team_total_goals",
  "team_total_points",
  "team_total_runs",
  "team_total_corners",
  "team_total_cards",
  "1st_half_team_total",
  "2nd_half_team_total",
  "1st_half_team_total_goals",
  "2nd_half_team_total_goals",
  "1st_half_team_total_points",
  "2nd_half_team_total_points",
  "1st_half_team_total_runs",
  "2nd_half_team_total_runs",
]);

const VALUE_TOTAL_KEYS = new Set([
  "total",
  "totals",
  "total_points",
  "total_goals",
  "total_runs",
  "total_games",
  "total_sets",
  "total_corners",
  "total_cards",
  "1st_half_total_goals",
  "2nd_half_total_goals",
  "1st_half_total_points",
  "2nd_half_total_points",
  "1st_half_total_runs",
  "2nd_half_total_runs",
  "over_under",
  "over/under",
]);

const VALUE_MONEYLINE_KEYS = new Set([
  "moneyline",
  "money_line",
  "match_winner",
  "winner",
  "1x2",
  "h2h",
  "draw_no_bet",
  "moneyline_3_way",
  "moneyline_3way",
  "map_winner",
  "map_moneyline",
  "set_winner",
  "set_moneyline",
]);

function snakeKey(value) {
  return safeStr(value)
    .toLowerCase()
    .replace(/[\s./-]+/g, "_")
    .replace(/__+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function readVerifiedRawFromRow(row) {
  if (!row || typeof row !== "object") return null;
  const ov = row.offerVerification && typeof row.offerVerification === "object" ? row.offerVerification : null;
  if (!ov) return null;
  const status = String(ov.status || "").trim();
  if (status !== "verified") return null;
  return ov;
}

function pickValueMarketTypeKey(row, verifiedRaw) {
  const fromVerified = verifiedRaw
    ? snakeKey(
        verifiedRaw.matchedMarketType ??
          verifiedRaw.matchedMarket_type ??
          verifiedRaw.matchedMarket ??
          ""
      )
    : "";
  if (fromVerified) return fromVerified;
  return snakeKey(row?.market_type ?? row?.marketType ?? row?.market ?? "");
}

/**
 * Returns the canonical Value market family for display purposes.
 * One of: "spread" | "team_total" | "total" | "moneyline" | "".
 *
 * Detection is based exclusively on the structured Sharp `market_type` key
 * (or the matched-raw equivalent when verified) to avoid false positives from
 * free-text `market` strings.
 */
function getValueMarketFamily(row, verifiedRaw) {
  const key = pickValueMarketTypeKey(row, verifiedRaw || readVerifiedRawFromRow(row));
  if (!key) return "";
  if (VALUE_HANDICAP_LIKE_KEYS.has(key)) return "spread";
  if (VALUE_TEAM_TOTAL_KEYS.has(key)) return "team_total";
  if (VALUE_TOTAL_KEYS.has(key)) return "total";
  if (VALUE_MONEYLINE_KEYS.has(key)) return "moneyline";

  // Loose contains-based fallback for unknown variants — still keeps the
  // display deterministic without leaking snake_case into the UI.
  if (/\b(spread|handicap|run_line|puck_line)\b/.test(key)) return "spread";
  if (/\bteam_total\b/.test(key)) return "team_total";
  if (/\btotal\b/.test(key) || /over_under|over\/under/.test(key)) return "total";
  if (/moneyline|match_winner|winner|h2h|1x2|draw_no_bet/.test(key)) return "moneyline";
  return "";
}

function isValueHandicapLikeMarket(row, verifiedRaw) {
  return getValueMarketFamily(row, verifiedRaw) === "spread";
}

function isValueTotalLikeMarket(row, verifiedRaw) {
  const f = getValueMarketFamily(row, verifiedRaw);
  return f === "total" || f === "team_total";
}

function pickValueDisplayLineSource(row, verifiedRaw) {
  if (verifiedRaw) {
    const fromMatched = firstNonEmptyString([
      verifiedRaw.matchedLine,
      verifiedRaw.matchedRaw && typeof verifiedRaw.matchedRaw === "object"
        ? verifiedRaw.matchedRaw.line
        : "",
    ]);
    if (fromMatched) return fromMatched;
  }
  return pickValueDisplayLine(row);
}

function formatValueDisplayLine(row, verifiedRaw, family) {
  const fam = family || getValueMarketFamily(row, verifiedRaw);
  if (fam === "moneyline") return "";
  const raw = pickValueDisplayLineSource(row, verifiedRaw);
  if (!raw) return "";
  if (fam === "spread") return formatSignedValueLine(raw);
  if (fam === "total" || fam === "team_total") return formatUnsignedValueLine(raw);
  return safeStr(raw);
}

function hasVerifiedValueMatchedFields(ov) {
  if (!ov || typeof ov !== "object") return false;
  const status = String(ov.status || "").trim();
  if (status !== "verified") return false;
  const mr = ov.matchedRaw && typeof ov.matchedRaw === "object" ? ov.matchedRaw : null;
  return Boolean(
    safeStr(ov.matchedMarket) ||
      safeStr(ov.matchedSelection) ||
      safeStr(ov.matchedSelectionType) ||
      safeStr(ov.matchedLine) ||
      ov.matchedOddsDecimal != null ||
      ov.matchedOddsAmerican != null ||
      (mr &&
        (safeStr(mr.market_type ?? mr.marketType ?? mr.market) ||
          safeStr(mr.selection) ||
          safeStr(mr.selection_type) ||
          safeStr(mr.line) ||
          mr.odds_decimal != null ||
          mr.odds_american != null))
  );
}

function normalizeValueSportKey(rawSport) {
  const s = safeStr(rawSport).toLowerCase();
  if (!s) return "";
  if (s === "soccer") return "football";
  return s;
}

function valueMarketLooksGenericFullMatchFamily(key) {
  if (!key) return false;
  if (VALUE_HANDICAP_LIKE_KEYS.has(key)) return true;
  if (VALUE_TEAM_TOTAL_KEYS.has(key)) return true;
  if (VALUE_TOTAL_KEYS.has(key)) return true;
  if (VALUE_MONEYLINE_KEYS.has(key)) return true;
  return /\b(total|team_total|spread|handicap|moneyline|winner|1x2|h2h|draw_no_bet)\b/.test(key);
}

function extractValueMarketPeriodScope(input) {
  const src = input && typeof input === "object" ? input : {};
  const sport = normalizeValueSportKey(src.sport);
  const lang = String(src.lang || "en").toLowerCase();
  const marketType = snakeKey(src.marketType ?? src.market_type ?? "");
  const market = snakeKey(src.market ?? "");
  const selectionType = snakeKey(src.selectionType ?? src.selection_type ?? "");
  const rawObj = src.raw && typeof src.raw === "object" ? src.raw : null;
  const rawMarketType = snakeKey(rawObj ? rawObj.market_type ?? rawObj.marketType ?? rawObj.market : "");
  const rawSelectionType = snakeKey(rawObj ? rawObj.selection_type ?? rawObj.selectionType : "");

  const hay = [
    marketType,
    market,
    selectionType,
    rawMarketType,
    rawSelectionType,
  ]
    .filter(Boolean)
    .join("_");

  if (!hay) return "";

  const has = (re) => re.test(hay);
  if (has(/(^|_)(1st_quarter|first_quarter|q1|quarter_1|1q)(_|$)/)) return "Q1";
  if (has(/(^|_)(2nd_quarter|second_quarter|q2|quarter_2|2q)(_|$)/)) return "Q2";
  if (has(/(^|_)(3rd_quarter|third_quarter|q3|quarter_3|3q)(_|$)/)) return "Q3";
  if (has(/(^|_)(4th_quarter|fourth_quarter|q4|quarter_4|4q)(_|$)/)) return "Q4";
  if (has(/(^|_)(1st_half|first_half|1h|half_1)(_|$)/)) return "1H";
  if (has(/(^|_)(2nd_half|second_half|2h|half_2)(_|$)/)) return "2H";

  if (has(/(^|_)(1st_set|set_1|set1|first_set)(_|$)/)) return "Set 1";
  if (has(/(^|_)(2nd_set|set_2|set2|second_set)(_|$)/)) return "Set 2";
  if (has(/(^|_)(3rd_set|set_3|set3|third_set)(_|$)/)) return "Set 3";

  if (has(/(^|_)(map_1|map1|first_map)(_|$)/)) return "Map 1";
  if (has(/(^|_)(map_2|map2|second_map)(_|$)/)) return "Map 2";
  if (has(/(^|_)(map_3|map3|third_map)(_|$)/)) return "Map 3";

  const primaryKey = marketType || rawMarketType || market;
  if (!valueMarketLooksGenericFullMatchFamily(primaryKey)) return "";

  if (sport === "tennis") return lang.startsWith("vi") ? "Trận" : "Match";
  if (sport === "football" || sport === "basketball" || sport === "esports") return "FT";
  return "";
}

function buildValueCanonicalFamilyLabel(rawMarketKey, lang) {
  const key = snakeKey(rawMarketKey);
  const bucket = pickValueDisplayLangBucket(lang);
  if (!key) return "";

  if (key.includes("moneyline_3_way") || key === "1x2" || key === "moneyline_3way") {
    return bucket.moneyline_3_way || bucket.moneyline;
  }
  if (key.includes("moneyline") || key.includes("winner") || key === "h2h" || key === "draw_no_bet") {
    return bucket.moneyline;
  }
  if (key.includes("team_total")) {
    if (key.includes("goals")) return bucket.team_total_goals;
    if (key.includes("points")) return bucket.team_total_points;
    if (key.includes("runs")) return bucket.team_total_runs;
    if (key.includes("corners")) return bucket.team_total_corners;
    return bucket.team_total;
  }
  if (key.includes("total")) {
    if (key.includes("points")) return String(lang || "").toLowerCase().startsWith("vi") ? "Tổng Điểm" : "Total Points";
    if (key.includes("goals")) return bucket.total_goals;
    if (key.includes("games")) return String(lang || "").toLowerCase().startsWith("vi") ? "Tổng số Game" : "Total Games";
    if (key.includes("sets")) return bucket.total_sets;
    if (key.includes("runs")) return bucket.total_runs;
    if (key.includes("corners")) return bucket.total_corners;
    return bucket.total;
  }
  if (key.includes("asian_handicap_corners") || key.includes("corners_asian_handicap")) {
    return bucket.asian_handicap_corners;
  }
  if (key.includes("corner_handicap") || key.includes("corners_handicap")) return bucket.corner_handicap;
  if (
    key.includes("spread") ||
    key.includes("handicap") ||
    key.includes("run_line") ||
    key.includes("puck_line")
  ) {
    return bucket.spread;
  }
  return "";
}

function normalizeTeamTotalSideToken(value) {
  const k = snakeKey(value);
  if (!k) return "";
  if (
    /(^|_)(home|home_team|team_home|home_total|home_team_total|home_over|home_under)(_|$)/.test(k) ||
    k === "home"
  ) {
    return "HOME";
  }
  if (
    /(^|_)(away|away_team|team_away|away_total|away_team_total|away_over|away_under)(_|$)/.test(k) ||
    k === "away"
  ) {
    return "AWAY";
  }
  if (k.includes("home")) return "HOME";
  if (k.includes("away")) return "AWAY";
  return "";
}

function resolveSideFromSelectionTeamName(selectionRaw, homeTeam, awayTeam) {
  const selection = safeStr(selectionRaw).toLowerCase();
  const home = safeStr(homeTeam).toLowerCase();
  const away = safeStr(awayTeam).toLowerCase();
  if (!selection || (!home && !away)) return "";
  const hasHome = home ? selection.includes(home) : false;
  const hasAway = away ? selection.includes(away) : false;
  if (hasHome && hasAway) return "AMBIGUOUS";
  if (home && (selection === home || selection.startsWith(`${home} `))) return "HOME";
  if (away && (selection === away || selection.startsWith(`${away} `))) return "AWAY";
  return "";
}

function normalizeTeamTotalDirectionToken(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
  if (!raw) return "";

  const tokens = raw.split(" ").filter(Boolean);
  const hasOver = tokens.includes("over") || tokens.includes("tai") || tokens.includes("o");
  const hasUnder = tokens.includes("under") || tokens.includes("xiu") || tokens.includes("u");
  if (hasOver && hasUnder) return "";
  if (hasOver) return "OVER";
  if (hasUnder) return "UNDER";
  return "";
}

function resolveValueTeamTotalDisplaySide(row, verifiedRaw) {
  const input = row && typeof row === "object" ? row : {};
  const ov = verifiedRaw || (input.offerVerification && typeof input.offerVerification === "object" ? input.offerVerification : null);
  const matchedRaw = ov && ov.matchedRaw && typeof ov.matchedRaw === "object" ? ov.matchedRaw : null;
  const useMatched = hasVerifiedValueMatchedFields(ov);

  const homeTeam = safeStr(input.home_team || input.homeTeam);
  const awayTeam = safeStr(input.away_team || input.awayTeam);

  const sideCandidates = useMatched
    ? [
        { source: "offerVerification.rawTeamTotalSide", value: ov?.rawTeamTotalSide },
        { source: "offerVerification.valueTeamTotalSide", value: ov?.valueTeamTotalSide },
        { source: "offerVerification.matchedSelectionType", value: ov?.matchedSelectionType },
        { source: "offerVerification.matchedRaw.selection_type", value: matchedRaw?.selection_type },
        { source: "offerVerification.matchedSelection", value: ov?.matchedSelection },
        { source: "offerVerification.matchedRaw.selection", value: matchedRaw?.selection },
      ]
    : [
        { source: "row.selection_type", value: input.selection_type },
        { source: "row.selectionType", value: input.selectionType },
        { source: "row.selection", value: input.selection },
        { source: "row.display_selection", value: input.display_selection },
        { source: "row.displaySelection", value: input.displaySelection },
      ];

  let side = "";
  let sideSource = "";
  let sideAmbiguous = false;
  for (let i = 0; i < sideCandidates.length; i += 1) {
    const c = sideCandidates[i];
    let cand = normalizeTeamTotalSideToken(c.value);
    if (!cand) {
      cand = resolveSideFromSelectionTeamName(c.value, homeTeam, awayTeam);
      if (cand === "AMBIGUOUS") {
        sideAmbiguous = true;
        continue;
      }
    }
    if (!cand) continue;
    if (!side) {
      side = cand;
      sideSource = c.source;
    } else if (side !== cand) {
      sideAmbiguous = true;
    }
  }

  const directionCandidates = useMatched
    ? [
        { source: "offerVerification.rawTeamTotalDirection", value: ov?.rawTeamTotalDirection },
        { source: "offerVerification.valueTeamTotalDirection", value: ov?.valueTeamTotalDirection },
        { source: "offerVerification.matchedSelectionType", value: ov?.matchedSelectionType },
        { source: "offerVerification.matchedRaw.selection_type", value: matchedRaw?.selection_type },
        { source: "offerVerification.matchedSelection", value: ov?.matchedSelection },
        { source: "offerVerification.matchedRaw.selection", value: matchedRaw?.selection },
      ]
    : [
        { source: "row.selection", value: input.selection },
        { source: "row.display_selection", value: input.display_selection },
        { source: "row.displaySelection", value: input.displaySelection },
        { source: "row.selection_type", value: input.selection_type },
        { source: "row.selectionType", value: input.selectionType },
      ];

  let direction = "";
  let directionSource = "";
  let directionAmbiguous = false;
  for (let i = 0; i < directionCandidates.length; i += 1) {
    const c = directionCandidates[i];
    const cand = normalizeTeamTotalDirectionToken(c.value);
    if (!cand) continue;
    if (!direction) {
      direction = cand;
      directionSource = c.source;
    } else if (direction !== cand) {
      directionAmbiguous = true;
    }
  }

  const lineRaw = useMatched
    ? firstNonEmptyString([
        ov?.matchedLine,
        matchedRaw ? matchedRaw.line : "",
        input.line,
      ])
    : firstNonEmptyString([input.line, input.point, input.handicap, input.total]);
  const line = formatUnsignedValueLine(lineRaw);

  if (sideAmbiguous || !side) {
    return {
      ok: false,
      teamName: "",
      side: "",
      direction,
      line: safeStr(line),
      source: useMatched ? "matched_raw" : "opportunity_source",
      reason: sideAmbiguous ? "side_ambiguous" : "side_missing",
      debug: {
        useMatched,
        sideSource,
        directionSource,
        teamTotalSideVerified: ov?.teamTotalSideVerified === true,
      },
    };
  }
  if (directionAmbiguous || !direction) {
    return {
      ok: false,
      teamName: "",
      side,
      direction: "",
      line: safeStr(line),
      source: useMatched ? "matched_raw" : "opportunity_source",
      reason: directionAmbiguous ? "direction_ambiguous" : "direction_missing",
      debug: {
        useMatched,
        sideSource,
        directionSource,
        teamTotalSideVerified: ov?.teamTotalSideVerified === true,
      },
    };
  }
  if (!line) {
    return {
      ok: false,
      teamName: "",
      side,
      direction,
      line: "",
      source: useMatched ? "matched_raw" : "opportunity_source",
      reason: "line_missing",
      debug: {
        useMatched,
        sideSource,
        directionSource,
        teamTotalSideVerified: ov?.teamTotalSideVerified === true,
      },
    };
  }

  const teamName = side === "HOME" ? homeTeam : side === "AWAY" ? awayTeam : "";
  if (!teamName) {
    return {
      ok: false,
      teamName: "",
      side,
      direction,
      line: safeStr(line),
      source: useMatched ? "matched_raw" : "opportunity_source",
      reason: "team_name_missing",
      debug: {
        useMatched,
        sideSource,
        directionSource,
        teamTotalSideVerified: ov?.teamTotalSideVerified === true,
      },
    };
  }

  return {
    ok: true,
    teamName,
    side,
    direction,
    line: safeStr(line),
    source: useMatched ? "matched_raw" : "opportunity_source",
    reason: "resolved",
    debug: {
      useMatched,
      sideSource,
      directionSource,
      teamTotalSideVerified: ov?.teamTotalSideVerified === true,
    },
  };
}

function buildValueCanonicalDisplay(row, lang) {
  if (!row || typeof row !== "object") {
    return {
      sourcePriority: "opportunity_source",
      rawMarketKey: "",
      rawSelection: "",
      rawSelectionType: "",
      rawLine: "",
      marketFamily: "",
      periodScope: "",
      marketLabel: "",
      selectionLabel: "",
      lineLabel: "",
      hkOddsLabel: "",
      debug: {},
    };
  }

  const ov = row.offerVerification && typeof row.offerVerification === "object" ? row.offerVerification : null;
  const matchedRaw = ov && ov.matchedRaw && typeof ov.matchedRaw === "object" ? ov.matchedRaw : null;
  const useMatched = hasVerifiedValueMatchedFields(ov);

  const rawMarketSource = useMatched
    ? firstNonEmptyString([
        ov.matchedMarket,
        matchedRaw ? matchedRaw.market_type : "",
        matchedRaw ? matchedRaw.marketType : "",
        matchedRaw ? matchedRaw.market : "",
      ])
    : firstNonEmptyString([row.market_type, row.marketType, row.market]);
  const rawSelection = useMatched
    ? firstNonEmptyString([ov.matchedSelection, matchedRaw ? matchedRaw.selection : ""])
    : firstNonEmptyString([row.selection, row.display_selection, row.displaySelection]);
  const rawSelectionType = useMatched
    ? firstNonEmptyString([ov.matchedSelectionType, matchedRaw ? matchedRaw.selection_type : ""])
    : firstNonEmptyString([row.selection_type, row.selectionType]);
  const rawLine = useMatched
    ? firstNonEmptyString([ov.matchedLine, matchedRaw ? matchedRaw.line : ""])
    : firstNonEmptyString([row.line, row.point, row.handicap, row.total]);

  const marketKey = snakeKey(rawMarketSource);
  const marketFamily = getValueMarketFamily(
    {
      market_type: rawMarketSource,
      market: rawMarketSource,
      selection_type: rawSelectionType,
    },
    null
  );
  const periodScope = extractValueMarketPeriodScope({
    sport: row.sport,
    marketType: rawMarketSource,
    market: rawMarketSource,
    selectionType: rawSelectionType,
    raw: useMatched ? matchedRaw : null,
    lang,
  });

  const familyLabel = buildValueCanonicalFamilyLabel(rawMarketSource, lang);
  const marketLabel = familyLabel ? (periodScope ? `${familyLabel} · ${periodScope}` : familyLabel) : "";

  const home = safeStr(row.home_team || row.homeTeam);
  const away = safeStr(row.away_team || row.awayTeam);
  const side = detectValueSideFromText(rawSelection, home, away);
  const ou = detectValueOuFromText(rawSelection) || detectValueOuFromText(rawSelectionType);
  const ouWord = pickValueOuWord(lang, ou);
  const lineSigned = formatSignedValueLine(rawLine);
  const lineUnsigned = formatUnsignedValueLine(rawLine);
  const teamTotalResolved = marketFamily === "team_total" ? resolveValueTeamTotalDisplaySide(row, useMatched ? ov : null) : null;

  let selectionLabel = "";
  if (marketFamily === "moneyline") {
    if (side === "draw") selectionLabel = pickValueDrawWord(lang);
    else if (side === "home" && home) selectionLabel = home;
    else if (side === "away" && away) selectionLabel = away;
    else selectionLabel = safeStr(rawSelection);
  } else if (marketFamily === "total") {
    if (ouWord && lineUnsigned) selectionLabel = `${ouWord} ${lineUnsigned}`.trim();
    else if (ouWord) selectionLabel = ouWord;
    else if (lineUnsigned) selectionLabel = lineUnsigned;
    else selectionLabel = safeStr(rawSelection);
  } else if (marketFamily === "team_total") {
    if (teamTotalResolved && teamTotalResolved.ok) {
      const dirToken = teamTotalResolved.direction === "UNDER" ? "under" : "over";
      const dirWord = pickValueOuWord(lang, dirToken);
      selectionLabel = [teamTotalResolved.teamName, dirWord, teamTotalResolved.line]
        .filter(Boolean)
        .join(" ")
        .trim();
    } else {
      const isVi = String(lang || "").toLowerCase().startsWith("vi");
      selectionLabel = isVi ? "Cần kiểm đội" : "Check team";
    }
  } else if (marketFamily === "spread") {
    let team = "";
    if (side === "home") team = home;
    else if (side === "away") team = away;
    else if (safeStr(rawSelection) && !valueLabelLooksRawSnakeCase(rawSelection)) team = safeStr(rawSelection);
    if (team && lineSigned) selectionLabel = `${team} ${lineSigned}`.replace(/\s+/g, " ").trim();
    else selectionLabel = team || lineSigned || safeStr(rawSelection);
  } else {
    selectionLabel = safeStr(rawSelection);
  }

  const dec = normalizeAnyOddsToDecimal(
    useMatched
      ? ov.matchedOddsDecimal ?? (matchedRaw ? matchedRaw.odds_decimal : null) ?? ov.matchedOddsAmerican ?? (matchedRaw ? matchedRaw.odds_american : null)
      : row.odds_decimal ?? row.oddsDecimal ?? row.odds_american ?? row.oddsAmerican ?? row.odds
  );
  const hkOddsLabel = formatHkFromDecimal(dec);
  const lineLabel = marketFamily === "moneyline" ? "" : marketFamily === "spread" ? lineSigned : lineUnsigned;

  return {
    sourcePriority: useMatched ? "matched_raw" : "opportunity_source",
    rawMarketKey: marketKey,
    rawSelection: safeStr(rawSelection),
    rawSelectionType: safeStr(rawSelectionType),
    rawLine: safeStr(rawLine),
    marketFamily: marketFamily || "",
    periodScope,
    marketLabel,
    selectionLabel: safeStr(selectionLabel),
    lineLabel: safeStr(lineLabel),
    hkOddsLabel: safeStr(hkOddsLabel),
    debug: {
      useMatchedRaw: useMatched,
      offerVerificationStatus: ov ? String(ov.status || "") : "",
      rawMarketSource: safeStr(rawMarketSource),
      teamTotalResolved,
    },
    teamTotalResolved,
  };
}

function buildValueDisplayPeriodScope(row) {
  return extractValueMarketPeriodScope({
    sport: row?.sport,
    marketType: row?.market_type ?? row?.marketType ?? row?.market,
    market: row?.market,
    selectionType: row?.selection_type ?? row?.selectionType,
    raw: row?.offerVerification && typeof row.offerVerification === "object" ? row.offerVerification.matchedRaw : null,
    lang: "en",
  });
}

const VALUE_DISPLAY_FAMILY_LABELS = {
  vi: {
    spread: "Cược Chấp",
    asian_spread: "Cược Chấp Châu Á",
    asian_handicap_corners: "Cược Chấp Góc Châu Á",
    corner_handicap: "Cược chấp phạt góc",
    total: "Tài/Xỉu",
    total_goals: "Tài/Xỉu bàn thắng",
    total_runs: "Tài/Xỉu runs",
    total_games: "Tài/Xỉu games",
    total_sets: "Tài/Xỉu sets",
    total_corners: "Tài/Xỉu phạt góc",
    team_total: "Tổng đội",
    team_total_goals: "Tổng bàn thắng đội",
    team_total_points: "Tổng điểm đội",
    team_total_runs: "Tổng runs đội",
    team_total_corners: "Tổng phạt góc đội",
    moneyline: "Kèo Thắng",
    moneyline_3_way: "Kèo thắng 3 cửa",
  },
  en: {
    spread: "Spread / Handicap",
    asian_spread: "Asian Handicap",
    asian_handicap_corners: "Asian Handicap Corners",
    corner_handicap: "Corners Handicap",
    total: "Total",
    total_goals: "Total Goals",
    total_runs: "Total Runs",
    total_games: "Total Games",
    total_sets: "Total Sets",
    total_corners: "Total Corners",
    team_total: "Team Total",
    team_total_goals: "Team Total Goals",
    team_total_points: "Team Total Points",
    team_total_runs: "Team Total Runs",
    team_total_corners: "Team Total Corners",
    moneyline: "Moneyline",
    moneyline_3_way: "3-Way Moneyline",
  },
};

function pickValueDisplayLangBucket(lang) {
  const s = String(lang || "").toLowerCase();
  if (s.startsWith("vi")) return VALUE_DISPLAY_FAMILY_LABELS.vi;
  return VALUE_DISPLAY_FAMILY_LABELS.en;
}

function buildValueDisplayMarketFamilyLabel(row, lang, verifiedRaw) {
  const key = pickValueMarketTypeKey(row, verifiedRaw || readVerifiedRawFromRow(row));
  const bucket = pickValueDisplayLangBucket(lang);
  if (!key) return "";
  if (key === "asian_handicap" || key === "european_handicap") return bucket.asian_spread || bucket.spread;
  if (key === "asian_handicap_corners" || key === "corners_asian_handicap") return bucket.asian_handicap_corners;
  if (key === "corner_handicap" || key === "corners_handicap") return bucket.corner_handicap;
  if (key === "moneyline_3_way" || key === "moneyline_3way" || key === "1x2") return bucket.moneyline_3_way;
  if (VALUE_MONEYLINE_KEYS.has(key)) return bucket.moneyline;
  if (VALUE_TEAM_TOTAL_KEYS.has(key)) {
    if (key.includes("goals")) return bucket.team_total_goals;
    if (key.includes("points")) return bucket.team_total_points;
    if (key.includes("runs")) return bucket.team_total_runs;
    if (key.includes("corners")) return bucket.team_total_corners;
    return bucket.team_total;
  }
  if (VALUE_TOTAL_KEYS.has(key)) {
    if (key.includes("goals")) return bucket.total_goals;
    if (key.includes("runs")) return bucket.total_runs;
    if (key.includes("games")) return bucket.total_games;
    if (key.includes("sets")) return bucket.total_sets;
    if (key.includes("corners")) return bucket.total_corners;
    return bucket.total;
  }
  if (VALUE_HANDICAP_LIKE_KEYS.has(key)) return bucket.spread;
  return "";
}

/**
 * Build the Value card market header label.
 *
 * Format: "<Family Label> · <Period>" — period is omitted when not detected.
 * Example: "Cược Chấp · FT" / "Tài/Xỉu · HT" / "Kèo Thắng" (no scope).
 *
 * The line is NEVER appended here — the line belongs on the selection row.
 */
function buildValueDisplayMarketLabel(row, lang, verifiedRaw) {
  if (!row || typeof row !== "object") return "";
  const c = buildValueCanonicalDisplay(
    verifiedRaw
      ? {
          ...row,
          offerVerification: verifiedRaw,
        }
      : row,
    lang
  );
  const canonical = safeStr(c.marketLabel);
  if (canonical) return canonical;
  const familyLabel = buildValueDisplayMarketFamilyLabel(row, lang, verifiedRaw);
  if (!familyLabel) return "";
  const scope = buildValueDisplayPeriodScope(row);
  if (!scope) return familyLabel;
  return `${familyLabel} · ${scope}`;
}

function pickValueOuWord(lang, ou) {
  const isVi = String(lang || "").toLowerCase().startsWith("vi");
  if (ou === "over") return isVi ? "Tài" : "Over";
  if (ou === "under") return isVi ? "Xỉu" : "Under";
  return "";
}

function pickValueDrawWord(lang) {
  const isVi = String(lang || "").toLowerCase().startsWith("vi");
  return isVi ? "Hòa" : "Draw";
}

function detectValueOuFromText(rawText) {
  const t = safeStr(rawText)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (!t) return "";
  if (/\bover\b|\btai\b|^o[\s_-]/.test(t)) return "over";
  if (/\bunder\b|\bxiu\b|^u[\s_-]/.test(t)) return "under";
  if (/^o$/.test(t)) return "over";
  if (/^u$/.test(t)) return "under";
  return "";
}

function detectValueSideFromText(rawText, home, away) {
  const t = safeStr(rawText).toLowerCase();
  if (!t) return "";
  if (/^home\b/.test(t) || t === "h") return "home";
  if (/^away\b/.test(t) || t === "a") return "away";
  if (/^draw\b/.test(t) || t === "x") return "draw";
  const h = safeStr(home).toLowerCase();
  const a = safeStr(away).toLowerCase();
  if (h && (t === h || t.startsWith(`${h} `))) return "home";
  if (a && (t === a || t.startsWith(`${a} `))) return "away";
  return "";
}

/**
 * Build the Value card selection label.
 *
 * - Handicap/spread: "<Team> <signed line>" (signed line, never absolute).
 * - Total: "<Tài|Xỉu> <unsigned line>".
 * - Team total: "<Team> <Tài|Xỉu> <unsigned line>".
 * - Moneyline: "<Team>" or "Hòa" / "Draw". No line.
 *
 * Source priority (per Task A):
 *   1. verifiedRaw.matched* (raw preflight match) for selection / line.
 *   2. row.selection / row.line (opportunity feed) when not verified.
 */
function buildValueDisplaySelectionLabel(row, verifiedRaw, lang) {
  if (!row || typeof row !== "object") return "";
  const c = buildValueCanonicalDisplay(
    verifiedRaw
      ? {
          ...row,
          offerVerification: verifiedRaw,
        }
      : row,
    lang
  );
  return safeStr(c.selectionLabel);
}

function buildValueDisplayBookName(row, verifiedRaw) {
  const ov = verifiedRaw || readVerifiedRawFromRow(row);
  if (ov) {
    const matched = firstNonEmptyString([
      ov.matchedSportsbook,
      ov.matchedRaw && typeof ov.matchedRaw === "object" ? ov.matchedRaw.sportsbook : "",
      ov.matchedRaw && typeof ov.matchedRaw === "object" ? ov.matchedRaw.bookmaker : "",
    ]);
    if (matched) return normalizeBookmakerNameConservative(matched);
  }
  const fromRow = firstNonEmptyString([row?.sportsbook, row?.bookmaker, row?.book]);
  return fromRow ? normalizeBookmakerNameConservative(fromRow) : "";
}

function buildValueDisplayDecimalOdds(row, verifiedRaw) {
  const ov = verifiedRaw || readVerifiedRawFromRow(row);
  if (ov) {
    const matchedDec = ov.matchedOddsDecimal ?? (ov.matchedRaw && ov.matchedRaw.odds_decimal);
    const dec = normalizeAnyOddsToDecimal(matchedDec);
    if (dec != null) return dec;
    const matchedAm = ov.matchedOddsAmerican ?? (ov.matchedRaw && ov.matchedRaw.odds_american);
    const decAm = normalizeAnyOddsToDecimal(matchedAm);
    if (decAm != null) return decAm;
  }
  return normalizeAnyOddsToDecimal(
    row?.odds_decimal ?? row?.oddsDecimal ?? row?.odds_american ?? row?.oddsAmerican ?? row?.odds
  );
}

function buildValueDisplayHkOdds(row, verifiedRaw) {
  const dec = buildValueDisplayDecimalOdds(row, verifiedRaw);
  return formatHkFromDecimal(dec);
}

/**
 * Returns the source label used to drive the dev audit log and the
 * Source Verified vs Raw Verified UI behaviour. Note: this only reflects
 * which fields the DISPLAY came from. It does not by itself authorize
 * "Vào cược" — the calling page must still gate that on the existing
 * preflight + readiness rules.
 */
function valueDisplaySourcePriority(row, verifiedRaw) {
  const ov = verifiedRaw || (row && row.offerVerification && typeof row.offerVerification === "object" ? row.offerVerification : null);
  return hasVerifiedValueMatchedFields(ov) ? "matched_raw" : "opportunity_source";
}

// ---------------------------------------------------------------------------
// Value RAW VERIFY (adapter-driven).
//
// `verifySingleValueOfferWithRawOdds` is the single source of truth for the
// Value publish gate against the per-event raw odds feed. server.js fetches
// raw rows for an event, then delegates the entire matching + repricing +
// EV-threshold gate to this adapter.
//
// The adapter performs strict structural identity matching on:
//   1) book (with safe alias normalization)
//   2) market family + subject + period/scope
//   3) selection / team / outcome
//   4) line (per family rules)
// and then:
//   5) chooses the freshest matched raw row
//   6) recomputes EV from `fair_probability * matchedRawDecimal`
//   7) publishes when recomputed EV >= threshold, otherwise rejects
//
// Source-row odds drift is NOT a hard reject: when a structural match exists
// but the price has moved, we reprice with the matched raw price and re-gate
// on EV. This replaces the previous strict "odds must equal source" rule.
// ---------------------------------------------------------------------------

const VALUE_LINE_EPSILON = 1e-4;

function valueAdapterNumOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function valueAdapterTrim(v) {
  return v == null ? "" : String(v).trim();
}

function valueAdapterNormalizeBookKey(s) {
  let k = valueAdapterTrim(s)
    .toLowerCase()
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/[^a-z0-9]+/g, "");
  if (!k) return "";
  if (k.endsWith("sportsbook") && k.length > "sportsbook".length + 2) {
    k = k.slice(0, -"sportsbook".length);
  }
  // Safe aliases (TASK B).
  if (k === "pin" || k === "pinnacle" || k === "pinnaclesports") return "pinnacle";
  if (k === "saba" || k === "sabasports") return "saba";
  if (k === "bet365us" || k === "bet365_us") return "bet365us";
  if (k === "bet365uk" || k === "bet365_uk") return "bet365uk";
  if (k === "betfair" || k === "betfairstp" || k === "betfairsb") return "betfair";
  if (k === "sbo" || k === "sbobet") return "sbobet";
  if (k === "marathon" || k === "marathonbet") return "marathonbet";
  if (k === "188" || k === "188bet") return "188bet";
  if (k === "1xbet" || k === "onexbet") return "1xbet";
  return k;
}

function valueAdapterBooksEquivalent(a, b) {
  const ka = valueAdapterNormalizeBookKey(a);
  const kb = valueAdapterNormalizeBookKey(b);
  return Boolean(ka && kb && ka === kb);
}

function valueAdapterMarketCanonical(s) {
  return valueAdapterTrim(s)
    .toLowerCase()
    .replace(/[\s\-./]+/g, "_")
    .replace(/__+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function valueAdapterMarketFamily(mtRaw) {
  const k = valueAdapterMarketCanonical(mtRaw);
  if (!k) return "";
  if (/(^|_)player(_|$)|player_prop|playerprop/.test(k)) return "player_prop";
  // corner_handicap is its own family — must NOT match generic spread/handicap.
  if (k.includes("corner_handicap") || k.includes("corners_handicap") || k.includes("asian_handicap_corners") || k.includes("corners_asian_handicap")) {
    return "corner_handicap";
  }
  if (k.includes("team_total") || (k.includes("team") && k.includes("total"))) return "team_total";
  if (k.includes("total") || k.includes("over_under") || k === "ou" || k === "o_u") return "total";
  if (k.includes("spread") || k.includes("handicap") || k.includes("run_line") || k.includes("puck_line") || k.includes("runline") || k.includes("puckline")) {
    return "spread";
  }
  if (
    k === "moneyline" ||
    k === "money_line" ||
    k === "h2h" ||
    k === "head_to_head" ||
    k === "match_winner" ||
    k === "winner" ||
    k === "1x2" ||
    k === "draw_no_bet" ||
    k === "dnb" ||
    k.includes("moneyline") ||
    k.includes("match_winner")
  ) {
    return "moneyline";
  }
  return k;
}

function valueAdapterMarketSubject(mtRaw) {
  const k = valueAdapterMarketCanonical(mtRaw);
  if (!k) return "generic";
  if (k.includes("corner")) return "corners";
  if (k.includes("card")) return "cards";
  if (k.includes("goal")) return "goals";
  if (k.includes("run")) return "runs";
  if (k.includes("point")) return "points";
  if (k.includes("game")) return "games";
  if (k.includes("set")) return "sets";
  return "generic";
}

function valueAdapterMarketScope(mtRaw, periodHint) {
  const tokens = `${valueAdapterMarketCanonical(mtRaw)} ${valueAdapterTrim(periodHint).toLowerCase().replace(/[\s\-./]+/g, "_")}`;
  if (/(^|_)(1st_half|first_half|1h|h1|1st_period|first_period|p1)(_|$)/.test(tokens)) return "1H";
  if (/(^|_)(2nd_half|second_half|2h|h2|2nd_period|second_period|p2)(_|$)/.test(tokens)) return "2H";
  if (/(^|_)(3rd_period|third_period|p3)(_|$)/.test(tokens)) return "P3";
  for (let q = 1; q <= 4; q += 1) {
    const re = new RegExp(`(^|_)(q${q}|${q}st_quarter|${q}nd_quarter|${q}rd_quarter|${q}th_quarter|first_quarter|second_quarter|third_quarter|fourth_quarter|quarter_${q})(_|$)`);
    if (re.test(tokens)) return `Q${q}`;
  }
  for (let m = 1; m <= 7; m += 1) {
    if (new RegExp(`(^|_)map_?${m}(_|$)`).test(tokens)) return `MAP${m}`;
  }
  for (let s = 1; s <= 7; s += 1) {
    if (new RegExp(`(^|_)set_?${s}(_|$)`).test(tokens)) return `SET${s}`;
  }
  for (let i = 1; i <= 9; i += 1) {
    if (new RegExp(`(^|_)inning_?${i}(_|$)|(^|_)${i}(st|nd|rd|th)_inning(_|$)`).test(tokens)) return `I${i}`;
  }
  return "FT";
}

function valueAdapterMarketsCompatible(srcMt, rawMt) {
  const srcK = valueAdapterMarketCanonical(srcMt);
  const rawK = valueAdapterMarketCanonical(rawMt);
  if (srcK && rawK && srcK === rawK) return true;
  const srcFam = valueAdapterMarketFamily(srcMt);
  const rawFam = valueAdapterMarketFamily(rawMt);
  if (!srcFam || !rawFam || srcFam !== rawFam) return false;
  // team_total, total, corner_handicap, spread, moneyline, player_prop families must not cross.
  const srcSubj = valueAdapterMarketSubject(srcMt);
  const rawSubj = valueAdapterMarketSubject(rawMt);
  if (srcSubj !== rawSubj) return false;
  return true;
}

function valueAdapterParseTimestampMs(v) {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) && v > 0 ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n) || n <= 0) return null;
    return s.length <= 10 ? n * 1000 : n;
  }
  const ms = Date.parse(s);
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

function valueAdapterRawTsMs(raw) {
  if (!raw || typeof raw !== "object") return null;
  const candidates = [
    raw.last_seen_at,
    raw.lastSeenAt,
    raw.odds_changed_at,
    raw.oddsChangedAt,
    raw.timestamp,
    raw.ts,
    raw.updated_at,
    raw.updatedAt,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const ms = valueAdapterParseTimestampMs(candidates[i]);
    if (ms != null) return ms;
  }
  return null;
}

function valueAdapterRawOddsDecimal(raw) {
  if (!raw || typeof raw !== "object") return null;
  const oddsObj = raw.odds && typeof raw.odds === "object" && !Array.isArray(raw.odds) ? raw.odds : null;
  const dec = valueAdapterNumOrNull(oddsObj?.decimal ?? raw.odds_decimal ?? raw.oddsDecimal);
  if (dec != null && dec > 1) return dec;
  const am = valueAdapterNumOrNull(oddsObj?.american ?? raw.odds_american ?? raw.oddsAmerican);
  if (am != null) {
    if (am >= 100) return 1 + am / 100;
    if (am <= -100) return 1 + 100 / Math.abs(am);
  }
  return null;
}

function valueAdapterRawOddsAmerican(raw) {
  if (!raw || typeof raw !== "object") return null;
  const oddsObj = raw.odds && typeof raw.odds === "object" && !Array.isArray(raw.odds) ? raw.odds : null;
  const am = valueAdapterNumOrNull(oddsObj?.american ?? raw.odds_american ?? raw.oddsAmerican);
  if (am != null) return am;
  const dec = valueAdapterRawOddsDecimal(raw);
  if (dec == null) return null;
  return americanFromDecimal(dec);
}

function valueAdapterLineEqual(a, b, signed) {
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  if (signed) return Math.abs(x - y) <= VALUE_LINE_EPSILON;
  return Math.abs(Math.abs(x) - Math.abs(y)) <= VALUE_LINE_EPSILON;
}

function valueAdapterLineHasExplicitSign(v) {
  if (v == null) return false;
  const s = String(v).trim();
  if (!s) return false;
  if (/^[-+]/.test(s)) return true;
  const n = Number(s);
  return Number.isFinite(n) && n < 0;
}

function valueAdapterLineMatches(family, srcLine, rawLine) {
  if (family === "moneyline") {
    const srcN = Number(srcLine);
    const rawN = Number(rawLine);
    const srcHas = srcLine != null && String(srcLine).trim() !== "" && Number.isFinite(srcN);
    const rawHas = rawLine != null && String(rawLine).trim() !== "" && Number.isFinite(rawN);
    if (srcHas || rawHas) return { ok: false, reason: "moneyline_unexpected_line" };
    return { ok: true };
  }
  const srcN = Number(srcLine);
  if (srcLine == null || String(srcLine).trim() === "" || !Number.isFinite(srcN)) {
    return { ok: false, reason: "missing_source_line" };
  }
  const rawN = Number(rawLine);
  if (rawLine == null || String(rawLine).trim() === "" || !Number.isFinite(rawN)) {
    return { ok: false, reason: "no_market_scope" };
  }
  if (family === "spread" || family === "corner_handicap") {
    const signed = valueAdapterLineHasExplicitSign(srcLine);
    return valueAdapterLineEqual(srcLine, rawLine, signed)
      ? { ok: true }
      : { ok: false, reason: "no_market_scope" };
  }
  // total / team_total / player_prop — unsigned absolute compare.
  return valueAdapterLineEqual(srcLine, rawLine, false)
    ? { ok: true }
    : { ok: false, reason: "no_market_scope" };
}

function valueAdapterDirectionToken(v) {
  const s = valueAdapterTrim(v)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return null;
  if (/^(over|o|tai)$/.test(s)) return "over";
  if (/^(under|u|xiu)$/.test(s)) return "under";
  if (/\bover\b/.test(s) && !/\bunder\b/.test(s)) return "over";
  if (/\bunder\b/.test(s) && !/\bover\b/.test(s)) return "under";
  return null;
}

function valueAdapterTeamToken(v) {
  return valueAdapterTrim(v)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[.,'"`]/g, "")
    .replace(/[()]/g, " ")
    .replace(/[-_–—]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function valueAdapterSideToken(v, home, away) {
  const t = valueAdapterTeamToken(v);
  if (!t) return null;
  if (t === "home" || t === "h" || t === "1") return "HOME";
  if (t === "away" || t === "a" || t === "2") return "AWAY";
  if (t === "draw" || t === "tie" || t === "x") return "DRAW";
  const homeTok = valueAdapterTeamToken(home);
  const awayTok = valueAdapterTeamToken(away);
  if (homeTok && awayTok && homeTok !== awayTok) {
    if (t === homeTok || t.startsWith(`${homeTok} `) || t.endsWith(` ${homeTok}`) || t.includes(` ${homeTok} `)) return "HOME";
    if (t === awayTok || t.startsWith(`${awayTok} `) || t.endsWith(` ${awayTok}`) || t.includes(` ${awayTok} `)) return "AWAY";
  }
  return null;
}

function valueAdapterTeamTotalImpliedSideFromMarket(mtRaw) {
  const k = valueAdapterMarketCanonical(mtRaw);
  if (!k) return null;
  const tokens = ` ${k.replace(/_/g, " ")} `;
  const hasHome = / home /.test(tokens);
  const hasAway = / away /.test(tokens);
  if (hasHome && !hasAway) return "HOME";
  if (hasAway && !hasHome) return "AWAY";
  return null;
}

function valueAdapterTeamTotalSide(candidates, home, away, mtRaw) {
  let side = null;
  let ambiguous = false;
  for (let i = 0; i < candidates.length; i += 1) {
    const t = valueAdapterTeamToken(candidates[i]);
    if (!t) continue;
    const parts = t.split(" ").filter(Boolean);
    let compoundSide = null;
    for (let j = 0; j < parts.length; j += 1) {
      const p = parts[j];
      if (p === "home" || p === "h") compoundSide = compoundSide || "HOME";
      else if (p === "away" || p === "a") compoundSide = compoundSide || "AWAY";
    }
    if (compoundSide) {
      if (!side) side = compoundSide;
      else if (side !== compoundSide) ambiguous = true;
    }
    const sd = valueAdapterSideToken(candidates[i], home, away);
    if (sd === "HOME" || sd === "AWAY") {
      if (!side) side = sd;
      else if (side !== sd) ambiguous = true;
    }
  }
  if (!side) {
    const implied = valueAdapterTeamTotalImpliedSideFromMarket(mtRaw);
    if (implied) side = implied;
  }
  return { side, ambiguous };
}

function valueAdapterSelectionMatch(family, row, raw) {
  if (family === "player_prop") {
    const srcP = valueAdapterTrim(row?.player_name ?? row?.playerName).toLowerCase();
    const rawP = valueAdapterTrim(raw?.player_name).toLowerCase();
    const srcS = valueAdapterTrim(row?.stat_category ?? row?.statCategory).toLowerCase();
    const rawS = valueAdapterTrim(raw?.stat_category).toLowerCase();
    if ((srcP || rawP) && srcP !== rawP) return { ok: false, reason: "no_selection" };
    if ((srcS || rawS) && srcS !== rawS) return { ok: false, reason: "no_selection" };
    const srcDir = valueAdapterDirectionToken(row?.selection ?? row?.selection_type ?? row?.selectionType);
    const rawDir = valueAdapterDirectionToken(raw?.selection ?? raw?.selection_type);
    if (srcDir && rawDir) {
      if (srcDir !== rawDir) return { ok: false, reason: "no_selection" };
    }
    return { ok: true };
  }

  if (family === "total") {
    const srcDir = valueAdapterDirectionToken(row?.selection ?? row?.selection_type ?? row?.selectionType);
    const rawDir = valueAdapterDirectionToken(raw?.selection ?? raw?.selection_type);
    if (!srcDir || !rawDir) return { ok: false, reason: "no_selection" };
    if (srcDir !== rawDir) return { ok: false, reason: "no_selection" };
    return { ok: true };
  }

  if (family === "team_total") {
    const srcDir = valueAdapterDirectionToken(row?.selection ?? row?.selection_type ?? row?.selectionType);
    const rawDir = valueAdapterDirectionToken(raw?.selection ?? raw?.selection_type);
    if (!srcDir || !rawDir) return { ok: false, reason: "no_selection" };
    if (srcDir !== rawDir) return { ok: false, reason: "no_selection" };
    const home = row?.home_team ?? row?.homeTeam ?? "";
    const away = row?.away_team ?? row?.awayTeam ?? "";
    const srcMt = row?.market_type ?? row?.marketType ?? row?.market ?? "";
    const rawMt = raw?.market_type ?? raw?.marketType ?? raw?.market ?? "";
    const srcSide = valueAdapterTeamTotalSide(
      [
        row?.selection,
        row?.display_selection,
        row?.displaySelection,
        row?.selection_type,
        row?.selectionType,
        srcMt,
      ],
      home,
      away,
      srcMt
    );
    const rawSide = valueAdapterTeamTotalSide(
      [raw?.selection, raw?.selection_type, rawMt],
      home,
      away,
      rawMt
    );
    if (srcSide.ambiguous || rawSide.ambiguous) {
      return { ok: false, reason: "team_total_side_ambiguous" };
    }
    if (!srcSide.side || !rawSide.side) {
      return { ok: false, reason: "team_total_side_ambiguous" };
    }
    if (srcSide.side !== rawSide.side) return { ok: false, reason: "no_selection" };
    return { ok: true };
  }

  if (family === "moneyline" || family === "spread" || family === "corner_handicap") {
    const home = row?.home_team ?? row?.homeTeam ?? "";
    const away = row?.away_team ?? row?.awayTeam ?? "";
    const srcSide =
      valueAdapterSideToken(row?.selection_type ?? row?.selectionType, home, away) ||
      valueAdapterSideToken(row?.selection, home, away);
    const rawSide =
      valueAdapterSideToken(raw?.selection_type, home, away) ||
      valueAdapterSideToken(raw?.selection, home, away);
    if (!srcSide || !rawSide) return { ok: false, reason: "no_selection" };
    if (srcSide !== rawSide) return { ok: false, reason: "no_selection" };
    return { ok: true };
  }

  // Unknown family: strict normalized text equality only.
  const srcSel = valueAdapterTrim(row?.selection).toLowerCase();
  const rawSel = valueAdapterTrim(raw?.selection).toLowerCase();
  if (srcSel && rawSel && srcSel === rawSel) return { ok: true };
  return { ok: false, reason: "no_selection" };
}

function valueAdapterIdentitySig(row) {
  if (!row || typeof row !== "object") return "";
  const e = valueAdapterTrim(row.event_id ?? row.game_id ?? row.fixture_id ?? row.match_id);
  const b = valueAdapterNormalizeBookKey(row.sportsbook ?? row.bookmaker ?? row.book);
  const mt = valueAdapterMarketCanonical(row.market_type ?? row.marketType ?? row.market);
  const sel = valueAdapterTrim(row.selection).toLowerCase();
  const ln = row.line == null ? "" : String(row.line).trim();
  const od = valueAdapterNumOrNull(row.odds_decimal ?? row.oddsDecimal ?? row.odds);
  return `${e}\x1f${b}\x1f${mt}\x1f${sel}\x1f${ln}\x1f${od ?? ""}`;
}

function valueAdapterDecimalFromAny(v) {
  const n = valueAdapterNumOrNull(v);
  if (n == null) return null;
  if (n > 1 && n < 100) return n;
  if (n >= 100) return 1 + n / 100;
  if (n <= -100) return 1 + 100 / Math.abs(n);
  if (n > 0 && n < 1) return 1 + n;
  return null;
}

function valueAdapterFairProbability(sourceRow) {
  if (!sourceRow || typeof sourceRow !== "object") return null;
  let fp = valueAdapterNumOrNull(sourceRow.fair_probability ?? sourceRow.fairProbability);
  if (fp != null) {
    if (fp > 1 && fp <= 100) fp = fp / 100;
    if (fp > 0 && fp <= 1) return fp;
  }
  const decCands = [
    sourceRow.no_vig_odds,
    sourceRow.noVigOdds,
    sourceRow.true_odds,
    sourceRow.trueOdds,
    sourceRow.fair_odds,
    sourceRow.fairOdds,
  ];
  for (let i = 0; i < decCands.length; i += 1) {
    const dec = valueAdapterDecimalFromAny(decCands[i]);
    if (dec != null && dec > 1) return 1 / dec;
  }
  return null;
}

/**
 * Recompute Value EV from `fair_probability * matchedRawDecimal`.
 *
 * `fair_probability` is taken from the source row (Sharp `/opportunities/ev`
 * payload). The raw decimal price is taken from the matched offer in
 * `/events/{id}/odds`. Source row odds drift is tolerated — the recomputed
 * EV is the only publish-gate signal in TASK A.
 */
function computeRepricedValueEv(sourceRow, matchedRaw) {
  const rawDec = valueAdapterRawOddsDecimal(matchedRaw);
  const rawAm = valueAdapterRawOddsAmerican(matchedRaw);
  const fairProbability = valueAdapterFairProbability(sourceRow);
  if (fairProbability == null) {
    return {
      ok: false,
      reason: "missing_fair_probability",
      evPercentage: null,
      fairProbability: null,
      rawOddsDecimal: rawDec,
      rawOddsAmerican: rawAm,
    };
  }
  if (rawDec == null) {
    return {
      ok: false,
      reason: "missing_raw_odds",
      evPercentage: null,
      fairProbability,
      rawOddsDecimal: null,
      rawOddsAmerican: rawAm,
    };
  }
  const evPercentage = (rawDec * fairProbability - 1) * 100;
  return {
    ok: true,
    reason: null,
    evPercentage,
    fairProbability,
    rawOddsDecimal: rawDec,
    rawOddsAmerican: rawAm,
  };
}

/**
 * Apply matched-raw repricing to a cloned source row. Matched raw odds become
 * the canonical display/copy/preflight odds. The original source-row odds
 * snapshot is preserved on `offerVerification.sourceOdds*` for diagnostics
 * only — never displayed.
 */
function applyValueRawRepricingToRow(sourceRow, matchedRaw, repricedEv, options) {
  const opts = options && typeof options === "object" ? options : {};
  const cloned = { ...sourceRow };
  const sourceDec = valueAdapterNumOrNull(sourceRow?.odds_decimal ?? sourceRow?.oddsDecimal ?? sourceRow?.odds);
  const sourceAm = valueAdapterNumOrNull(sourceRow?.odds_american ?? sourceRow?.oddsAmerican);
  const dec = repricedEv.rawOddsDecimal;
  const am =
    repricedEv.rawOddsAmerican != null
      ? repricedEv.rawOddsAmerican
      : dec != null
        ? americanFromDecimal(dec)
        : null;
  if (dec != null) {
    cloned.odds_decimal = dec;
    cloned.oddsDecimal = dec;
    cloned.odds = dec;
  }
  if (am != null) {
    cloned.odds_american = am;
    cloned.oddsAmerican = am;
  }
  if (matchedRaw && matchedRaw.line != null && String(matchedRaw.line).trim() !== "") {
    cloned.line = matchedRaw.line;
  }
  const matchedBook = valueAdapterTrim(matchedRaw?.sportsbook ?? matchedRaw?.bookmaker);
  if (matchedBook) {
    cloned.sportsbook = matchedBook;
    cloned.bookmaker = matchedBook;
    cloned.book = matchedBook;
  }
  if (matchedRaw?.selection_type != null && valueAdapterTrim(matchedRaw.selection_type)) {
    cloned.selection_type = matchedRaw.selection_type;
    cloned.selectionType = matchedRaw.selection_type;
  }
  const lastSeen = matchedRaw?.last_seen_at ?? matchedRaw?.lastSeenAt ?? null;
  const oddsChanged = matchedRaw?.odds_changed_at ?? matchedRaw?.oddsChangedAt ?? null;
  if (lastSeen != null) cloned.rawLastSeenAt = lastSeen;
  if (oddsChanged != null) cloned.rawOddsChangedAt = oddsChanged;

  cloned.ev_percentage = repricedEv.evPercentage;
  cloned.ev = repricedEv.evPercentage;
  cloned.EV = repricedEv.evPercentage;
  cloned.isRawOfferVerified = true;
  cloned.isSourceVerified = false;
  cloned.betReadiness = "raw_ready";
  cloned.lifecycleStatus = "active";

  const decimalDelta = sourceDec != null && dec != null ? dec - sourceDec : null;
  const americanDelta = sourceAm != null && am != null ? am - sourceAm : null;
  const direction =
    decimalDelta == null
      ? "same"
      : Math.abs(decimalDelta) <= 1e-9
        ? "same"
        : decimalDelta > 0
          ? "higher"
          : "lower";

  cloned.offerVerification = {
    status: "verified",
    verificationLevel: "raw",
    reason: "raw_repriced",
    verifiedAt: opts.verifiedAt != null ? opts.verifiedAt : Date.now(),
    matchedSportsbook: matchedBook || null,
    matchedMarket:
      valueAdapterTrim(matchedRaw?.market_type ?? matchedRaw?.marketType ?? matchedRaw?.market) || null,
    matchedSelection: valueAdapterTrim(matchedRaw?.selection) || null,
    matchedSelectionType: valueAdapterTrim(matchedRaw?.selection_type) || null,
    matchedLine: matchedRaw && matchedRaw.line != null ? matchedRaw.line : null,
    matchedOddsDecimal: dec,
    matchedOddsAmerican: am,
    rawLastSeenAt: lastSeen != null ? String(lastSeen) : null,
    rawOddsChangedAt: oddsChanged != null ? String(oddsChanged) : null,
    sourceOddsDecimal: sourceDec,
    sourceOddsAmerican: sourceAm,
    sourceEvPercentage:
      opts.sourceEvPercentage != null
        ? opts.sourceEvPercentage
        : valueAdapterNumOrNull(sourceRow?.ev_percentage ?? sourceRow?.ev),
    recomputedEvPercentage: repricedEv.evPercentage,
    fairProbability: repricedEv.fairProbability,
    rawAgeSeconds: opts.rawAgeSeconds != null ? opts.rawAgeSeconds : null,
    sourceAgeSeconds: opts.sourceAgeSeconds != null ? opts.sourceAgeSeconds : null,
    oddsDrift: {
      decimalDelta: decimalDelta != null ? Number(decimalDelta.toFixed(6)) : null,
      americanDelta: americanDelta != null ? Number(americanDelta.toFixed(2)) : null,
      direction,
    },
    matchedRaw,
  };
  return cloned;
}

/**
 * Single-row Value publish gate against per-event raw odds.
 *
 * Returns a verdict shaped to match the legacy server-side verdict so the
 * existing `filterValueBetsByRawOfferVerification` orchestrator can consume it
 * without further mapping. The new fields beyond the legacy shape are:
 *   - `row`                   : repriced row when ok, null otherwise.
 *   - `recomputedEvPercentage`: EV computed from matched raw odds.
 *   - `sourceEvPercentage`    : original Sharp EV (for diagnostics/debug).
 */
function verifySingleValueOfferWithRawOdds(row, rawRows, options) {
  const opts = options && typeof options === "object" ? options : {};
  const nowMs = typeof opts.nowMs === "number" && Number.isFinite(opts.nowMs) ? opts.nowMs : Date.now();
  const minEvPct = valueAdapterNumOrNull(opts.minEvPct) != null ? valueAdapterNumOrNull(opts.minEvPct) : 0;
  const maxRawAgeSec = valueAdapterNumOrNull(opts.maxRawAgeSec);
  const allowMissingTs = opts.allowMissingTimestamp === true;
  const attemptedRawEventKeys = Array.isArray(opts.attemptedRawEventKeys)
    ? opts.attemptedRawEventKeys.slice()
    : [];
  const rawEventKeyUsed = opts.rawEventKeyUsed != null ? opts.rawEventKeyUsed : null;
  const sourceAgeSeconds = valueAdapterNumOrNull(opts.sourceAgeSeconds);

  const id = valueAdapterTrim(row?.id ?? row?.bet_id ?? row?.valueBetId);
  const identitySig = valueAdapterIdentitySig(row);
  const sourceEvPct = valueAdapterNumOrNull(row?.ev_percentage ?? row?.ev ?? row?.EV);

  const baseVerdict = (extra) => ({
    ok: false,
    row: null,
    matchedRaw: null,
    offerVerification: null,
    reason: null,
    finalStage: "rejected",
    finalReason: null,
    rawFailureReason: null,
    graceEligible: false,
    identitySig,
    id,
    sameBookRawRows: 0,
    sameMarketScopeRows: 0,
    sameSelectionRows: 0,
    structuralCandidates: 0,
    oddsMatchedCandidates: 0,
    candidateDiagnostics: [],
    attemptedRawEventKeys: attemptedRawEventKeys.slice(),
    rawEventKeyUsed,
    recomputedEvPercentage: null,
    sourceEvPercentage: sourceEvPct,
    ...(extra || {}),
  });

  const rejectWithOv = (extra) => {
    const v = baseVerdict(extra);
    const reason = v.reason || v.finalReason || "rejected";
    v.offerVerification = {
      status: "rejected",
      reason,
      verifiedAt: null,
      matchedSportsbook: extra && extra.matchedRaw ? valueAdapterTrim(extra.matchedRaw.sportsbook) || null : null,
      matchedMarket:
        extra && extra.matchedRaw
          ? valueAdapterTrim(extra.matchedRaw.market_type ?? extra.matchedRaw.market) || null
          : null,
      matchedSelection: extra && extra.matchedRaw ? valueAdapterTrim(extra.matchedRaw.selection) || null : null,
      matchedSelectionType:
        extra && extra.matchedRaw ? valueAdapterTrim(extra.matchedRaw.selection_type) || null : null,
      matchedLine: extra && extra.matchedRaw && extra.matchedRaw.line != null ? extra.matchedRaw.line : null,
      matchedOddsDecimal: extra && extra.matchedRaw ? valueAdapterRawOddsDecimal(extra.matchedRaw) : null,
      matchedOddsAmerican: extra && extra.matchedRaw ? valueAdapterRawOddsAmerican(extra.matchedRaw) : null,
      rawLastSeenAt:
        extra && extra.matchedRaw && extra.matchedRaw.last_seen_at != null
          ? String(extra.matchedRaw.last_seen_at)
          : null,
      rawOddsChangedAt:
        extra && extra.matchedRaw && extra.matchedRaw.odds_changed_at != null
          ? String(extra.matchedRaw.odds_changed_at)
          : null,
      sourceOddsDecimal: valueAdapterNumOrNull(row?.odds_decimal ?? row?.oddsDecimal ?? row?.odds),
      sourceOddsAmerican: valueAdapterNumOrNull(row?.odds_american ?? row?.oddsAmerican),
      sourceEvPercentage: sourceEvPct,
      recomputedEvPercentage: v.recomputedEvPercentage,
      fairProbability: extra && extra.fairProbability != null ? extra.fairProbability : null,
      rawAgeSeconds: extra && extra.rawAgeSeconds != null ? extra.rawAgeSeconds : null,
      sourceAgeSeconds,
      rawFailureReason: v.rawFailureReason,
      attemptedRawEventKeys: attemptedRawEventKeys.slice(),
      rawEventKeyUsed,
    };
    if (extra && extra.matchedRaw) v.matchedRaw = extra.matchedRaw;
    return v;
  };

  if (!Array.isArray(rawRows)) {
    return baseVerdict({
      reason: "raw_fetch_failed",
      finalStage: "raw_fetch",
      finalReason: "raw_fetch_failed",
      rawFailureReason: "raw_fetch_failed",
      graceEligible: true,
    });
  }

  const srcBook = row?.sportsbook ?? row?.bookmaker ?? row?.book ?? "";
  const srcMt = row?.market_type ?? row?.marketType ?? row?.market ?? "";
  const family = valueAdapterMarketFamily(srcMt);
  const subject = valueAdapterMarketSubject(srcMt);
  const scope = valueAdapterMarketScope(srcMt, row?.period);
  const srcLine = row?.line;

  const sameBookRawRows = [];
  for (let i = 0; i < rawRows.length; i += 1) {
    const r = rawRows[i];
    if (!r || typeof r !== "object") continue;
    if (valueAdapterBooksEquivalent(r.sportsbook ?? r.bookmaker, srcBook)) sameBookRawRows.push(r);
  }
  if (sameBookRawRows.length === 0) {
    return rejectWithOv({
      reason: "no_same_book",
      finalStage: "book",
      finalReason: "no_same_book",
      rawFailureReason: "no_same_book",
      graceEligible: true,
    });
  }

  const sameMarketScopeRows = [];
  for (let i = 0; i < sameBookRawRows.length; i += 1) {
    const r = sameBookRawRows[i];
    const rMt = r.market_type ?? r.marketType ?? r.market ?? "";
    const rFam = valueAdapterMarketFamily(rMt);
    const rSubj = valueAdapterMarketSubject(rMt);
    const rScope = valueAdapterMarketScope(rMt, r.period);
    if (family && rFam && family !== rFam) continue;
    if (subject !== rSubj) continue;
    if (scope !== rScope) continue;
    if (!valueAdapterMarketsCompatible(srcMt, rMt)) continue;
    sameMarketScopeRows.push(r);
  }
  if (sameMarketScopeRows.length === 0) {
    return rejectWithOv({
      reason: "no_market_scope",
      finalStage: "market",
      finalReason: "no_market_scope",
      rawFailureReason: "no_market_scope",
      graceEligible: true,
      sameBookRawRows: sameBookRawRows.length,
    });
  }

  const sameSelectionRows = [];
  let sawTeamTotalAmbiguous = false;
  for (let i = 0; i < sameMarketScopeRows.length; i += 1) {
    const r = sameMarketScopeRows[i];
    const m = valueAdapterSelectionMatch(family, row, r);
    if (!m.ok) {
      if (m.reason === "team_total_side_ambiguous") sawTeamTotalAmbiguous = true;
      continue;
    }
    sameSelectionRows.push(r);
  }
  if (sameSelectionRows.length === 0) {
    if (sawTeamTotalAmbiguous) {
      return rejectWithOv({
        reason: "team_total_side",
        finalStage: "team_total_side",
        finalReason: "team_total_side_ambiguous",
        rawFailureReason: "team_total_side_ambiguous",
        graceEligible: true,
        sameBookRawRows: sameBookRawRows.length,
        sameMarketScopeRows: sameMarketScopeRows.length,
      });
    }
    return rejectWithOv({
      reason: "no_selection",
      finalStage: "selection",
      finalReason: "no_selection",
      rawFailureReason: "no_selection",
      graceEligible: true,
      sameBookRawRows: sameBookRawRows.length,
      sameMarketScopeRows: sameMarketScopeRows.length,
    });
  }

  const structuralCandidates = [];
  let lineFailReason = null;
  for (let i = 0; i < sameSelectionRows.length; i += 1) {
    const r = sameSelectionRows[i];
    const lm = valueAdapterLineMatches(family, srcLine, r.line);
    if (!lm.ok) {
      if (lm.reason === "missing_source_line") lineFailReason = "missing_source_line";
      else if (lm.reason === "moneyline_unexpected_line" && !lineFailReason) {
        lineFailReason = "moneyline_unexpected_line";
      }
      continue;
    }
    structuralCandidates.push(r);
  }
  if (structuralCandidates.length === 0) {
    const reason =
      lineFailReason === "missing_source_line"
        ? "missing_source_line"
        : lineFailReason === "moneyline_unexpected_line"
          ? "no_market_scope"
          : "no_market_scope";
    return rejectWithOv({
      reason,
      finalStage: "line",
      finalReason: reason,
      rawFailureReason: reason,
      graceEligible: true,
      sameBookRawRows: sameBookRawRows.length,
      sameMarketScopeRows: sameMarketScopeRows.length,
      sameSelectionRows: sameSelectionRows.length,
    });
  }

  // Choose the freshest matching raw row.
  let bestRaw = null;
  let bestTs = -Infinity;
  let tiedAtBest = 0;
  let anyTs = false;
  for (let i = 0; i < structuralCandidates.length; i += 1) {
    const r = structuralCandidates[i];
    const ts = valueAdapterRawTsMs(r);
    if (ts == null) continue;
    anyTs = true;
    if (ts > bestTs) {
      bestRaw = r;
      bestTs = ts;
      tiedAtBest = 1;
    } else if (ts === bestTs) {
      tiedAtBest += 1;
    }
  }
  if (!anyTs) {
    if (allowMissingTs && structuralCandidates.length === 1) {
      bestRaw = structuralCandidates[0];
      bestTs = -Infinity;
    } else if (structuralCandidates.length > 1) {
      return rejectWithOv({
        reason: "ambiguous_raw_match",
        finalStage: "freshness",
        finalReason: "ambiguous_raw_match",
        rawFailureReason: "ambiguous_raw_match",
        sameBookRawRows: sameBookRawRows.length,
        sameMarketScopeRows: sameMarketScopeRows.length,
        sameSelectionRows: sameSelectionRows.length,
        structuralCandidates: structuralCandidates.length,
      });
    } else {
      return rejectWithOv({
        reason: "missing_raw_ts",
        finalStage: "raw_ts",
        finalReason: "missing_raw_ts",
        rawFailureReason: "missing_raw_ts",
        sameBookRawRows: sameBookRawRows.length,
        sameMarketScopeRows: sameMarketScopeRows.length,
        sameSelectionRows: sameSelectionRows.length,
        structuralCandidates: structuralCandidates.length,
      });
    }
  } else if (tiedAtBest > 1) {
    // Multiple rows share the same `last_seen_at`. Prefer rows with the most
    // recent `odds_changed_at` as a secondary freshness key. If two or more
    // rows still tie AND quote distinct prices, treat as ambiguous_raw_match
    // (we cannot pick a winner without an arbitrary choice). If they all
    // quote the same price, the choice is harmless and we pick the first one.
    const tiedRows = [];
    let bestOc = -Infinity;
    for (let i = 0; i < structuralCandidates.length; i += 1) {
      const r = structuralCandidates[i];
      const ts = valueAdapterRawTsMs(r);
      if (ts !== bestTs) continue;
      const oc = valueAdapterParseTimestampMs(r.odds_changed_at ?? r.oddsChangedAt);
      const ocKey = oc != null ? oc : -Infinity;
      if (ocKey > bestOc) bestOc = ocKey;
      tiedRows.push({ r, ocKey });
    }
    const stillTied = tiedRows.filter((x) => x.ocKey === bestOc).map((x) => x.r);
    if (stillTied.length === 1) {
      bestRaw = stillTied[0];
    } else {
      const distinctPrices = new Set();
      for (let i = 0; i < stillTied.length; i += 1) {
        const dec = valueAdapterRawOddsDecimal(stillTied[i]);
        if (dec != null) distinctPrices.add(Number(dec.toFixed(6)));
      }
      if (distinctPrices.size <= 1) {
        bestRaw = stillTied[0];
      } else {
        return rejectWithOv({
          reason: "ambiguous_raw_match",
          finalStage: "freshness",
          finalReason: "ambiguous_raw_match",
          rawFailureReason: "ambiguous_raw_match",
          matchedRaw: bestRaw,
          sameBookRawRows: sameBookRawRows.length,
          sameMarketScopeRows: sameMarketScopeRows.length,
          sameSelectionRows: sameSelectionRows.length,
          structuralCandidates: structuralCandidates.length,
        });
      }
    }
  }

  const rawAgeSec = bestTs > 0 && Number.isFinite(bestTs) ? (nowMs - bestTs) / 1000 : null;
  if (
    maxRawAgeSec != null &&
    rawAgeSec != null &&
    Number.isFinite(rawAgeSec) &&
    rawAgeSec > maxRawAgeSec
  ) {
    return rejectWithOv({
      reason: "stale_raw",
      finalStage: "stale_raw",
      finalReason: "stale_raw",
      rawFailureReason: "stale_raw",
      matchedRaw: bestRaw,
      rawAgeSeconds: rawAgeSec,
      sameBookRawRows: sameBookRawRows.length,
      sameMarketScopeRows: sameMarketScopeRows.length,
      sameSelectionRows: sameSelectionRows.length,
      structuralCandidates: structuralCandidates.length,
    });
  }

  const reprice = computeRepricedValueEv(row, bestRaw);
  if (!reprice.ok) {
    return rejectWithOv({
      reason: reprice.reason || "missing_fair_probability",
      finalStage: "reprice",
      finalReason: reprice.reason || "missing_fair_probability",
      rawFailureReason: reprice.reason || "missing_fair_probability",
      matchedRaw: bestRaw,
      fairProbability: reprice.fairProbability,
      rawAgeSeconds: rawAgeSec,
      sameBookRawRows: sameBookRawRows.length,
      sameMarketScopeRows: sameMarketScopeRows.length,
      sameSelectionRows: sameSelectionRows.length,
      structuralCandidates: structuralCandidates.length,
    });
  }

  if (reprice.evPercentage < minEvPct) {
    const v = rejectWithOv({
      reason: "ev_below_threshold_after_raw",
      finalStage: "ev_threshold",
      finalReason: "ev_below_threshold_after_raw",
      rawFailureReason: "ev_below_threshold_after_raw",
      matchedRaw: bestRaw,
      fairProbability: reprice.fairProbability,
      rawAgeSeconds: rawAgeSec,
      sameBookRawRows: sameBookRawRows.length,
      sameMarketScopeRows: sameMarketScopeRows.length,
      sameSelectionRows: sameSelectionRows.length,
      structuralCandidates: structuralCandidates.length,
    });
    v.recomputedEvPercentage = reprice.evPercentage;
    if (v.offerVerification) v.offerVerification.recomputedEvPercentage = reprice.evPercentage;
    return v;
  }

  const verifiedRow = applyValueRawRepricingToRow(row, bestRaw, reprice, {
    verifiedAt: nowMs,
    rawAgeSeconds: rawAgeSec,
    sourceAgeSeconds,
    sourceEvPercentage: sourceEvPct,
  });

  return {
    ok: true,
    row: verifiedRow,
    matchedRaw: bestRaw,
    offerVerification: verifiedRow.offerVerification,
    reason: null,
    finalStage: "matched",
    finalReason: "verified",
    rawFailureReason: null,
    graceEligible: false,
    identitySig,
    id,
    sameBookRawRows: sameBookRawRows.length,
    sameMarketScopeRows: sameMarketScopeRows.length,
    sameSelectionRows: sameSelectionRows.length,
    structuralCandidates: structuralCandidates.length,
    oddsMatchedCandidates: 1,
    candidateDiagnostics: [],
    attemptedRawEventKeys: attemptedRawEventKeys.slice(),
    rawEventKeyUsed,
    recomputedEvPercentage: reprice.evPercentage,
    sourceEvPercentage: sourceEvPct,
  };
}

/**
 * Lightweight per-row debug projection used by the existing Value debug routes.
 * Returns only the fields documented in TASK D when present on the verdict.
 */
function getValueRawVerifyDebugRow(verdict) {
  if (!verdict || typeof verdict !== "object") return null;
  const ov = verdict.offerVerification && typeof verdict.offerVerification === "object" ? verdict.offerVerification : {};
  return {
    id: verdict.id || null,
    ok: verdict.ok === true,
    finalStage: verdict.finalStage || null,
    finalReason: verdict.finalReason || null,
    rawFailureReason: verdict.rawFailureReason || null,
    sourceOddsDecimal: ov.sourceOddsDecimal ?? null,
    matchedRawOddsDecimal: ov.matchedOddsDecimal ?? null,
    sourceEvPercentage: ov.sourceEvPercentage ?? verdict.sourceEvPercentage ?? null,
    recomputedEvPercentage: ov.recomputedEvPercentage ?? verdict.recomputedEvPercentage ?? null,
    fairProbability: ov.fairProbability ?? null,
    oddsDriftDirection: ov.oddsDrift && ov.oddsDrift.direction ? ov.oddsDrift.direction : null,
    rawAgeSeconds: ov.rawAgeSeconds ?? null,
    sourceAgeSeconds: ov.sourceAgeSeconds ?? null,
  };
}

module.exports = {
  fetchRawEvOpportunities,
  fetchSharpValueBetList,
  normalizeValueBetRow,
  pickPassthroughValueState,
  enrichCommercialValueBetRow,
  buildValueDedupKeyFromNormalizedRow,
  validateValuePreflightMatchedRaw,
  valuePreflightServerContractOk,
  isSabaLiveNbaQ1PeriodValueRow,
  buildVerifiedValueCopyText,
  extractDecimalOddsFromValueSide,
  pickValueDisplayMarketSource,
  pickValueDisplaySelectionSource,
  pickValueDisplayLine,
  buildValueSemanticMarketLabel,
  buildValueSemanticSelectionLabel,
  buildValueDisplayOddsHk,
  buildValueDisplayTrueOddsHk,
  humanizeSnakeCaseForDisplay,
  valueLabelLooksRawSnakeCase,
  // Generic Value display helpers (Task B / D).
  getValueMarketFamily,
  isValueHandicapLikeMarket,
  isValueTotalLikeMarket,
  pickValueDisplayLineSource,
  formatValueDisplayLine,
  buildValueDisplayMarketLabel,
  buildValueDisplaySelectionLabel,
  buildValueDisplayBookName,
  buildValueDisplayDecimalOdds,
  buildValueDisplayHkOdds,
  valueDisplaySourcePriority,
  extractValueMarketPeriodScope,
  buildValueCanonicalDisplay,
  resolveValueTeamTotalDisplaySide,
  readVerifiedRawFromRow,
  // Value RAW VERIFY (adapter-driven publish gate).
  verifySingleValueOfferWithRawOdds,
  computeRepricedValueEv,
  applyValueRawRepricingToRow,
  getValueRawVerifyDebugRow,
};
